import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  buildMultiflowVozSoftphoneUrl,
  MFV_SOFTPHONE_EVENT,
  resolveMultiflowVozBaseUrl,
  resolveSoftphoneUiOrigin,
} from "../../utils/voiceSoftphone";
import { postTicketCallLog } from "../../utils/voiceCallTicket";
import {
  playCalling,
  playRinging,
  stopAllVoiceSounds,
  stopAllVoiceSoundsAndSuppress,
  suppressVoiceRing,
  unlockVoiceAudio,
  isVoiceRingingPlaying,
} from "../../utils/voiceRingtone";
import {
  clearVoiceDesktopAlert,
  ensureVoiceNotificationPermission,
  maybeRequestVoiceNotifyOnGesture,
  showVoiceDesktopNotification,
} from "../../utils/voiceDesktopNotify";
import { buildChannelVoiceIvrForSoftphone } from "../../utils/voiceIvrChannel";
import DialerContactPicker from "../DialerContactPicker";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

const POS_KEY = "multiflow_voz_widget_position";
const WIDGET_ID = "multiflow_voz_widget";
/** Dedup de POST /call/ticket-log (URA + fila + Official no mesmo callId). */
const ticketLogOnce = new Set();

function looksLikeLid(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 14;
}

/** IDs de filas do usuário (User.queues do Auth). */
function getAgentQueueIds(user) {
  if (!user) return [];
  const fromQueues = Array.isArray(user.queues)
    ? user.queues.map((q) => Number(q?.id ?? q)).filter((n) => Number.isFinite(n) && n > 0)
    : [];
  const fromIds = Array.isArray(user.queueIds)
    ? user.queueIds.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0)
    : [];
  return Array.from(new Set([...fromQueues, ...fromIds]));
}

/**
 * URA/fila: toca para quem está no setor (UserQueues / user.queues).
 * Super/admin sem a fila NÃO deve ouvir Financeiro no lugar da Denise.
 */
function agentBelongsToQueue(user, queueId) {
  const qid = Number(queueId || 0);
  if (!qid) return false;
  const agentQueues = getAgentQueueIds(user);
  return agentQueues.includes(qid);
}

/**
 * Conexão (Whatsapp): canais em `channels` já vêm filtrados por voiceUserIds.
 */
function agentCanUseVoiceConnection(channels, whatsappId) {
  const id = Number(whatsappId || 0);
  if (!id) return true;
  const list = Array.isArray(channels) ? channels : [];
  if (!list.length) return false;
  return list.some((c) => Number(c?.id) === id);
}

/** Fila do setor: Auth.queues OU lista do servidor. Nunca só eligible (UserQueue incompleto). */
function agentMayRingIvrQueue(user, channels, { queueId, whatsappId, eligibleUserIds } = {}) {
  if (agentBelongsToQueue(user, queueId)) return true;
  const me = Number(user?.id || 0);
  if (Array.isArray(eligibleUserIds) && eligibleUserIds.length > 0 && me) {
    return eligibleUserIds.some((id) => Number(id) === me);
  }
  return false;
}

function logTicketCallFromSoftphone(payload) {
  const phone = String(payload.phone || "").replace(/\D/g, "");
  const lid = String(payload.peerLid || "").replace(/\D/g, "");
  if (!phone && !lid && !payload.contactId) return;
  const st = payload.status || "ended";
  const cid = String(payload.callId || "").trim();
  if (cid && (st === "started" || st === "answered")) {
    const key = `${cid}:${st}`;
    if (ticketLogOnce.has(key)) return;
    ticketLogOnce.add(key);
    if (ticketLogOnce.size > 80) {
      const first = ticketLogOnce.values().next().value;
      ticketLogOnce.delete(first);
    }
  }
  postTicketCallLog({
    direction: payload.direction === "inbound" ? "inbound" : "outbound",
    status: payload.status || "ended",
    callId: payload.callId || undefined,
    phone: phone || undefined,
    lid: lid || undefined,
    whatsappId: payload.whatsappId || undefined,
    contactId: payload.contactId || undefined,
    ticketId: payload.ticketId || undefined,
    queueId: payload.queueId ? Number(payload.queueId) : undefined,
  }).catch((err) => {
    console.error("[MultiflowVoz] ticket call log:", err?.message || err);
  });
}

async function resolveMultiflowContactLabel({ peer, peerName, peerLid, phone }) {
  const lid = String(peerLid || "").replace(/\D/g, "") ||
    (looksLikeLid(peer) ? String(peer).replace(/\D/g, "") : "");
  const number = String(phone || peer || "").replace(/\D/g, "");
  const queryNumber = looksLikeLid(number) ? "" : number;

  if (!lid && !queryNumber) {
    return {
      name: peerName || "",
      peer: peerName || peer || phone || "",
      phone: queryNumber || "",
    };
  }

  try {
    const { data } = await api.get("/contacts/resolve-peer", {
      params: {
        ...(lid ? { lid } : {}),
        ...(queryNumber ? { number: queryNumber } : {}),
      },
    });
    if (data?.name) {
      return {
        name: data.name,
        phone: data.number || queryNumber || "",
        peer: data.number
          ? `${data.name} · ${data.number}`
          : data.name,
      };
    }
  } catch {
    /* contato inexistente — usa fallback */
  }

  return {
    name: peerName || "",
    peer: peerName
      ? queryNumber
        ? `${peerName} · ${queryNumber}`
        : peerName
      : queryNumber || peer || "WhatsApp",
    phone: queryNumber || "",
  };
}

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark" || theme.palette.mode === "dark";
  return {
    root: {
      position: "fixed",
      zIndex: 13000,
      display: "flex",
      flexDirection: "column",
      borderRadius: 16,
      overflow: "hidden",
      background: dark ? "#1e1e1e" : "#fff",
      boxShadow: "0 12px 40px rgba(15, 23, 42, 0.28)",
      border: `1px solid ${theme.palette.divider || "rgba(0,0,0,0.08)"}`,
      userSelect: "none",
    },
    rootDefault: {
      right: 24,
      bottom: 96,
    },
    rootExpanded: {
      width: 360,
      height: 620,
    },
    rootExpandedCoex: {
      height: 640,
    },
    rootMinimized: {
      width: 280,
      height: "auto",
      minHeight: 64,
      borderRadius: 14,
      cursor: "grab",
      "&:active": { cursor: "grabbing" },
    },
    chrome: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "8px 10px",
      background: dark ? "#2a2a2a" : "#f1f5f9",
      borderBottom: `1px solid ${theme.palette.divider || "rgba(0,0,0,0.08)"}`,
      cursor: "grab",
      flexShrink: 0,
      "&:active": { cursor: "grabbing" },
    },
    chromeTitle: {
      fontSize: 12,
      fontWeight: 700,
      color: dark ? "#e2e8f0" : "#0f172a",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      minWidth: 0,
    },
    chromeActions: {
      display: "flex",
      alignItems: "center",
      gap: 2,
      flexShrink: 0,
    },
    chromeBtn: {
      border: 0,
      background: "transparent",
      width: 28,
      height: 28,
      borderRadius: 8,
      cursor: "pointer",
      color: dark ? "#94a3b8" : "#64748b",
      fontSize: 18,
      lineHeight: 1,
      display: "grid",
      placeItems: "center",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
        color: dark ? "#e2e8f0" : "#0f172a",
      },
    },
    iframeWrap: {
      flex: 1,
      minHeight: 0,
      position: "relative",
      overflow: "hidden",
    },
    contactOverlay: {
      position: "absolute",
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      zIndex: 40,
      pointerEvents: "auto",
    },
    iframe: {
      border: 0,
      width: "100%",
      height: "100%",
      background: "transparent",
      display: "block",
      overflow: "hidden",
    },
    iframeHidden: {
      position: "absolute",
      width: 1,
      height: 1,
      opacity: 0,
      pointerEvents: "none",
      overflow: "hidden",
    },
    mini: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
      cursor: "pointer",
    },
    miniDot: {
      width: 10,
      height: 10,
      borderRadius: "50%",
      flexShrink: 0,
      background: "#94a3b8",
    },
    miniDotRinging: {
      background: "#f59e0b",
      boxShadow: "0 0 0 4px rgba(245,158,11,0.25)",
      animation: "$pulse 1.2s ease-in-out infinite",
    },
    miniDotActive: {
      background: "#22c55e",
      boxShadow: "0 0 0 4px rgba(34,197,94,0.25)",
    },
    miniText: {
      minWidth: 0,
      flex: 1,
    },
    miniStatus: {
      fontSize: 11,
      fontWeight: 600,
      color: dark ? "#94a3b8" : "#64748b",
      lineHeight: 1.2,
    },
    miniPeer: {
      fontSize: 13,
      fontWeight: 700,
      color: dark ? "#f1f5f9" : "#0f172a",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    miniTimer: {
      fontSize: 13,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: dark ? "#e2e8f0" : "#334155",
      flexShrink: 0,
    },
    miniHangup: {
      width: 34,
      height: 34,
      borderRadius: "50%",
      border: 0,
      background: "#ef4444",
      color: "#fff",
      cursor: "pointer",
      flexShrink: 0,
      fontSize: 14,
      boxShadow: "0 4px 12px rgba(239,68,68,0.35)",
      transform: "rotate(135deg)",
      "&:hover": { filter: "brightness(1.05)" },
    },
    coexBanner: {
      margin: 0,
      padding: "6px 10px 8px",
      fontSize: 11,
      lineHeight: 1.35,
      color: dark ? "#fdba74" : "#9a3412",
      background: dark ? "rgba(154,52,18,0.25)" : "#fff7ed",
      borderBottom: dark
        ? "1px solid rgba(251,146,60,0.35)"
        : "1px solid #fdba74",
    },
    transferPick: {
      padding: "8px 10px",
      borderBottom: `1px solid ${theme.palette.divider || "rgba(0,0,0,0.08)"}`,
      background: dark ? "#252525" : "#f8fafc",
      maxHeight: 220,
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      gap: 6,
    },
    transferPickTitle: {
      fontSize: 11,
      fontWeight: 700,
      color: dark ? "#e2e8f0" : "#334155",
    },
    transferSearch: {
      width: "100%",
      boxSizing: "border-box",
      border: `1px solid ${theme.palette.divider || "#cbd5e1"}`,
      borderRadius: 8,
      padding: "7px 10px",
      fontSize: 12,
      outline: "none",
      background: dark ? "#1e1e1e" : "#fff",
      color: dark ? "#f1f5f9" : "#0f172a",
    },
    transferPickList: {
      maxHeight: 140,
      overflowY: "auto",
      flex: "1 1 auto",
    },
    transferPickBtn: {
      display: "block",
      width: "100%",
      textAlign: "left",
      border: `1px solid ${theme.palette.divider || "#cbd5e1"}`,
      background: dark ? "#1e1e1e" : "#fff",
      color: dark ? "#f1f5f9" : "#0f172a",
      borderRadius: 8,
      padding: "6px 8px",
      marginBottom: 4,
      cursor: "pointer",
      fontSize: 12,
    },
    transferPickEmpty: {
      fontSize: 11,
      color: dark ? "#94a3b8" : "#64748b",
      padding: "8px 2px",
    },
    transferWaiting: {
      padding: "10px 12px",
      borderBottom: `1px solid ${theme.palette.divider || "rgba(0,0,0,0.08)"}`,
      background: dark ? "rgba(0,51,158,0.25)" : "#eef2ff",
      color: dark ? "#c7d2fe" : "#1e3a8a",
      fontSize: 12,
      lineHeight: 1.4,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: 10,
      width: "100%",
      boxSizing: "border-box",
    },
    transferWaitingDot: {
      width: 10,
      height: 10,
      borderRadius: "50%",
      background: "#00339e",
      flexShrink: 0,
      animation: "$pulse 1.2s ease-in-out infinite",
    },
    transferWaitingText: {
      flex: "1 1 auto",
      minWidth: 0,
      overflow: "hidden",
    },
    transferWaitingTitle: {
      fontWeight: 700,
      fontSize: 13,
      marginBottom: 2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    transferWaitingSub: {
      fontSize: 11,
      opacity: 0.95,
      lineHeight: 1.35,
      whiteSpace: "normal",
      wordBreak: "normal",
      overflowWrap: "break-word",
    },
    answerBtn: {
      flex: "0 0 auto",
      border: 0,
      background: "#16a34a",
      color: "#fff",
      borderRadius: 8,
      padding: "8px 14px",
      margin: 0,
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 700,
      whiteSpace: "nowrap",
      boxShadow: "0 2px 8px rgba(22,163,74,0.35)",
      "&:hover": { filter: "brightness(1.05)" },
    },
    "@keyframes pulse": {
      "0%, 100%": { opacity: 1 },
      "50%": { opacity: 0.55 },
    },
  };
});

function formatTimer(seconds) {
  const mm = String(Math.floor((seconds || 0) / 60)).padStart(2, "0");
  const ss = String((seconds || 0) % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function statusLabel(state) {
  if (state?.status === "incoming") return "Chamada recebida…";
  if (state?.status === "ivr") return "URA em atendimento…";
  if (state?.status === "queue_ring" || state?.status === "queue_hold")
    return state?.queueLabel
      ? `Fila ${state.queueLabel}: tocando…`
      : "Fila: tocando…";
  if (state?.status === "ringing") return "Chamando no WhatsApp…";
  if (state?.status === "active") return "Em chamada";
  if (state?.status === "transferring" || state?.transferring)
    return state?.transferTargetName
      ? `Transferindo → ${state.transferTargetName}…`
      : "Transferindo…";
  if (state?.status === "transfer_offer") return "Transferência recebida…";
  if (state?.status === "pending_transfer") return "Transferência recebida… abrindo canal…";
  return "Pronto para ligar";
}

/** Fases em que o softphone deve ficar aberto para o atendente (após/fora da URA). */
function isAgentFacingStatus(status) {
  return (
    status === "queue_ring" ||
    status === "queue_hold" ||
    status === "active" ||
    status === "incoming" ||
    status === "transfer_offer" ||
    status === "pending_transfer" ||
    status === "ringing"
  );
}

/**
 * Softphone embutido no Multiflow:
 * painel flutuante arrastável + minimizar com resumo da chamada.
 */
const MultiflowVozWidget = ({
  token,
  coexOutboundOnly = false,
  whatsappId = null,
  currentUserId = null,
  channels = [],
  onSwitchChannel = null,
}) => {
  const classes = useStyles();
  const muiTheme = useTheme();
  const uiTheme =
    muiTheme?.palette?.type === "dark" || muiTheme?.palette?.mode === "dark"
      ? "dark"
      : "light";
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [src, setSrc] = useState("");
  /** Token realmente carregado no iframe (pode ficar “preso” se houver chamada/URA). */
  const frameTokenRef = useRef(null);
  const pendingFrameTokenRef = useRef(null);
  const frameWhatsappIdRef = useRef(null);
  const pendingFrameWhatsappIdRef = useRef(null);
  const [transferTargets, setTransferTargets] = useState(null);
  const [transferPickMeta, setTransferPickMeta] = useState(null);
  const [transferSearch, setTransferSearch] = useState("");
  const [transferWaiting, setTransferWaiting] = useState(null);
  const [dialerContactOpen, setDialerContactOpen] = useState(false);
  const [callState, setCallState] = useState({
    status: "idle",
    phone: "",
    name: "",
    peer: "",
    callId: "",
    seconds: 0,
    inCall: false,
    systemName: "Multiflow Voz",
    transferring: false,
    transferTargetName: "",
  });
  const [position, setPosition] = useState(null);
  const postToIframeRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const openRef = useRef(false);
  const iframeRef = useRef(null);
  const positionRef = useRef(null);
  const didDragRef = useRef(false);
  const loggedCallIdsRef = useRef(new Set());
  const phoneAlertedRef = useRef(new Set());
  const openWithRef = useRef(null);
  const callStateRef = useRef(callState);
  /** Usuário abriu o softphone de propósito (discador / FAB) — URA em background não fecha. */
  const agentPinnedOpenRef = useRef(false);

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  const forceAgentSoftphoneOpen = useCallback(() => {
    setOpen((o) => (o ? o : true));
    setMinimized((m) => (m ? false : m));
    setOpen(true);
    setMinimized(false);
    // Corrige posição inválida só 1x (não reseta toda hora — evita “pulo” na tela)
    setPosition((prev) => {
      if (!prev || typeof window === "undefined") return prev ?? null;
      const w = window.innerWidth || 0;
      const h = window.innerHeight || 0;
      if (
        !Number.isFinite(prev.x) ||
        !Number.isFinite(prev.y) ||
        prev.x < 8 ||
        prev.y < 8 ||
        prev.x > w - 120 ||
        prev.y > h - 80
      ) {
        try {
          localStorage.removeItem(POS_KEY);
        } catch {
          /* ignore */
        }
        return null;
      }
      return prev;
    });
  }, []);

  /** Se já estamos na fila/ativa, não esconder de novo por evento residual de URA. */
  const shouldKeepSoftphoneForAgent = useCallback((incomingCallId) => {
    const s = callStateRef.current;
    if (!s || !isAgentFacingStatus(s.status)) return false;
    if (s.status === "incoming" || s.status === "transfer_offer" || s.status === "ringing") {
      return false; // esses já abrem sozinhos; não são “proteger de URA”
    }
    // queue_ring / queue_hold / active
    if (!incomingCallId || !s.callId) return true;
    return String(s.callId) === String(incomingCallId);
  }, []);

  const rememberLoggedCall = (callId, status) => {
    const key = `${callId || ""}:${status || ""}`;
    if (!callId) return false;
    if (loggedCallIdsRef.current.has(key)) return true;
    loggedCallIdsRef.current.add(key);
    if (loggedCallIdsRef.current.size > 50) {
      const first = loggedCallIdsRef.current.values().next().value;
      loggedCallIdsRef.current.delete(first);
    }
    return false;
  };

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const channelRejectsCalls = useCallback(
    (waId) => {
      const list = Array.isArray(channels) ? channels : [];
      const ch =
        list.find((c) => Number(c.id) === Number(waId || whatsappId)) ||
        list.find((c) => String(c.wavoip) === String(token)) ||
        null;
      return String(ch?.rejectCalls || "").toLowerCase() === "enabled";
    },
    [channels, whatsappId, token]
  );

  const notifyAutoReject = useCallback(
    async ({ phone, callId, peerName, lid, waId } = {}) => {
      const targetWa = waId || whatsappId;
      if (!targetWa) return;
      try {
        await api.post("/call/auto-reject-notify", {
          whatsappId: targetWa,
          phone: phone || "",
          callId: callId || null,
          peerName: peerName || null,
          lid: lid || null,
        });
      } catch (err) {
        console.warn(
          "[MultiflowVoz] auto-reject-notify:",
          err?.message || err
        );
      }
    },
    [whatsappId]
  );

  // Poll no Multiflow (1+ canais): iframe é engasgado com aba minimizada.
  // Completa o socket voice-incoming e troca canal se a ring for de outro mfv.
  const lastMultiHitRef = useRef("");
  const toastOnceRef = useRef("");
  const answeredCallIdsRef = useRef(new Set());
  const ringQuietUntilRef = useRef(0);

  const quietVoiceRing = useCallback((ms = 5000) => {
    ringQuietUntilRef.current = Date.now() + ms;
    stopAllVoiceSoundsAndSuppress();
    clearVoiceDesktopAlert();
  }, []);
  /** IDs em que Meta/VOZ já encerrou — impede iframe “state” ressuscitar timer. */
  const endedCallIdsRef = useRef(new Set());

  const markCallEndedId = useCallback((callId) => {
    const id = String(callId || "").trim();
    if (!id) return;
    endedCallIdsRef.current.add(id);
    if (endedCallIdsRef.current.size > 60) {
      const first = endedCallIdsRef.current.values().next().value;
      endedCallIdsRef.current.delete(first);
    }
  }, []);

  const forceSoftphoneIdle = useCallback(
    (callIdHint) => {
      if (callIdHint) markCallEndedId(callIdHint);
      const cur = callStateRef.current?.callId;
      if (cur) markCallEndedId(cur);
      quietVoiceRing(4000);
      const idle = {
        status: "idle",
        callId: "",
        peer: "",
        phone: "",
        name: "",
        seconds: 0,
        inCall: false,
        systemName: "Multiflow Voz",
        transferring: false,
        transferTargetName: "",
        queueId: null,
        queueLabel: null,
        remainingSec: null,
        round: null,
      };
      setCallState((s) => ({ ...s, ...idle }));
      callStateRef.current = {
        ...callStateRef.current,
        ...idle,
      };
      try {
        postToIframeRef.current?.({ type: "hangup" });
      } catch {
        /* ignore */
      }
    },
    [markCallEndedId, quietVoiceRing]
  );

  const markCallAnswered = useCallback((callId) => {
    const id = String(callId || "").trim();
    if (id) {
      answeredCallIdsRef.current.add(id);
      if (answeredCallIdsRef.current.size > 40) {
        const first = answeredCallIdsRef.current.values().next().value;
        answeredCallIdsRef.current.delete(first);
      }
    }
    try {
      // remove toasts de fila/URA que ficavam “tocando” após Atender
      toast.dismiss();
    } catch {
      /* ignore */
    }
    quietVoiceRing(8000);
  }, [quietVoiceRing]);

  /** Libera callId para transferência: o mesmo ID continua vivo no destino. */
  const clearCallAnswered = useCallback((callId) => {
    const id = String(callId || "").trim();
    if (id) answeredCallIdsRef.current.delete(id);
  }, []);

  /** Bloqueia re-toque de fila se já atendeu esta (ou qualquer) chamada ativa. */
  const shouldBlockQueueRing = useCallback((callId) => {
    if (Date.now() < ringQuietUntilRef.current) return true;
    const st = callStateRef.current?.status;
    if (st === "active" || st === "ivr") return true;
    const id = String(callId || "").trim();
    if (id && answeredCallIdsRef.current.has(id)) {
      return true;
    }
    if (
      id &&
      callStateRef.current?.status === "active" &&
      String(callStateRef.current?.callId || "") === id
    ) {
      return true;
    }
    return false;
  }, []);

  const notifyCallOnce = useCallback((callKey, message, opts = {}) => {
    const key = String(callKey || message || "");
    if (!key || toastOnceRef.current === key) return;
    toastOnceRef.current = key;
    try {
      toast.info(message, {
        autoClose: 8000,
        toastId: `mfv-${key}`,
        onClick: () => {
          unlockVoiceAudio();
          // não rearmar toque se já atendeu (clique no toast residual)
          const st = callStateRef.current?.status;
          if (
            st !== "queue_ring" &&
            st !== "queue_hold" &&
            st !== "incoming" &&
            st !== "transfer_offer"
          ) {
            return;
          }
          playRinging().catch(() => {});
        },
        ...opts,
      });
    } catch {
      /* ignore */
    }
  }, []);

  const ivrInjectTimersRef = useRef([]);

  const cancelIvrInjectRetries = useCallback(() => {
    (ivrInjectTimersRef.current || []).forEach((t) => clearTimeout(t));
    ivrInjectTimersRef.current = [];
  }, []);

  const injectIvrRetries = useCallback((payload) => {
    cancelIvrInjectRetries();
    const post = () => {
      // Não re-injeta URA se o atendente já está na fila / chamada
      const st = callStateRef.current?.status;
      if (
        st === "queue_ring" ||
        st === "queue_hold" ||
        st === "active" ||
        st === "transfer_offer"
      ) {
        return;
      }
      postToIframeRef.current?.(payload);
    };
    post();
    ivrInjectTimersRef.current = [
      setTimeout(post, 400),
      setTimeout(post, 1000),
      setTimeout(post, 2000),
      setTimeout(post, 4000),
      setTimeout(post, 7000),
    ];
  }, [cancelIvrInjectRetries]);

  /**
   * URA em background: por padrão esconde o painel para o atendente.
   * Se o usuário abriu o softphone de propósito (agentPinnedOpenRef), mantém
   * aberto para discar outra ligação enquanto a URA segue no servidor/hub.
   * return true = URA tratada em background (não é popup de atendente).
   * return false = softphone deve ficar aberto para o agente (fila/activa).
   */
  const hideSoftphoneForIvrOnly = useCallback(
    (incomingCallId) => {
      if (shouldKeepSoftphoneForAgent(incomingCallId)) {
        forceAgentSoftphoneOpen();
        return false;
      }
      const st = callStateRef.current?.status;
      if (
        st === "queue_ring" ||
        st === "queue_hold" ||
        st === "active"
      ) {
        forceAgentSoftphoneOpen();
        return false;
      }
      // Discador / FAB: não fecha e não “rouba” o softphone com inject de URA
      if (agentPinnedOpenRef.current || openRef.current) {
        return true;
      }
      setOpen(false);
      setMinimized(true);
      stopAllVoiceSounds();
      clearVoiceDesktopAlert();
      return true;
    },
    [shouldKeepSoftphoneForAgent, forceAgentSoftphoneOpen]
  );

  const armIvrOnHub = useCallback(
    async (callId, waId) => {
      if (!callId) return null;
      try {
        const { data } = await api.post("/call/ivr-arm", {
          callId,
          whatsappId: waId || whatsappId || undefined,
        });
        return data?.voiceIvr?.enabled ? data.voiceIvr : null;
      } catch (err) {
        console.warn("[MultiflowVoz] ivr-arm:", err?.message || err);
        return null;
      }
    },
    [whatsappId]
  );

  const buildIvrCfgFromAny = useCallback((ch, armed, hubIvr) => {
    const base = String(process.env.REACT_APP_BACKEND_URL || "").replace(
      /\/$/,
      ""
    );
    const fromCh = buildChannelVoiceIvrForSoftphone(ch, base);
    if (fromCh?.enabled) return fromCh;
    if (armed?.enabled) {
      return {
        enabled: true,
        phase: "pending",
        claimClientId: null,
        timeoutSec: Number(armed.timeoutSec) || 15,
        hasWelcomeAudio: Boolean(armed.welcomeAudioUrl),
        welcomeAudioUrl: armed.welcomeAudioUrl || null,
        options: Array.isArray(armed.options) ? armed.options : [],
        fallbackQueueId: armed.fallbackQueueId || null,
        inputMode: armed.inputMode || "speech",
        speechHint: armed.speechHint || null,
      };
    }
    if (hubIvr?.enabled) {
      return {
        enabled: true,
        phase: "pending",
        claimClientId: null,
        timeoutSec: Number(hubIvr.timeoutSec) || 15,
        hasWelcomeAudio: Boolean(hubIvr.hasWelcomeAudio || hubIvr.welcomeAudioUrl),
        welcomeAudioUrl: hubIvr.welcomeAudioUrl || null,
        options: Array.isArray(hubIvr.options) ? hubIvr.options : [],
        fallbackQueueId: hubIvr.fallbackQueueId || null,
        inputMode: hubIvr.inputMode || "speech",
        speechHint: hubIvr.speechHint || null,
      };
    }
    return null;
  }, []);

  useEffect(() => {
    const mfvList = (Array.isArray(channels) ? channels : []).filter(
      (c) => c?.isMfv || String(c?.wavoip || "").toLowerCase().startsWith("mfv_")
    );
    if (!mfvList.length || !token) return undefined;

    let cancelled = false;
    let lastIvrInjectAt = 0;
    const openAgentQueueRing = (opts) => {
      const {
        callIdHit,
        peer,
        phone,
        peerName,
        queueId,
        label,
        whatsappId: wa,
      } = opts;
      // Pós-URA: só quem faz parte da fila (ex.: Suporte ≠ Financeiro)
      // e tem permissão na conexão (voiceUserIds — Oficial7700 só Daniel, etc.)
      if (
        !agentMayRingIvrQueue(user, channels, {
          queueId,
          whatsappId: wa || whatsappId,
        })
      ) {
        console.info(
          "[MultiflowVoz] fila/conexão ignorada",
          { queueId, whatsappId: wa || whatsappId, userId: user?.id }
        );
        return;
      }
      if (shouldBlockQueueRing(callIdHit)) return;
      cancelIvrInjectRetries();
      suppressVoiceRing(false);
      unlockVoiceAudio();
      if (!isVoiceRingingPlaying()) {
        playRinging().catch(() => {});
      }
      forceAgentSoftphoneOpen();
      requestAnimationFrame(() => forceAgentSoftphoneOpen());
      setCallState({
        status: "queue_ring",
        callId: callIdHit || "",
        peer: peer || peerName || phone || "",
        phone: phone || "",
        name: peerName || "",
        seconds: 0,
        inCall: true,
        systemName: "Multiflow Voz",
        queueId: queueId || null,
        queueLabel: label || null,
      });
      // sync imediato no ref (evita race de hide por event residual)
      callStateRef.current = {
        ...callStateRef.current,
        status: "queue_ring",
        callId: callIdHit || "",
        inCall: true,
        queueId: queueId || null,
        queueLabel: label || null,
      };
      postToIframeRef.current?.({
        type: "server-ivr-queue-ring",
        callId: callIdHit || "",
        peerNumber: phone || "",
        peerName: peerName || "",
        queueId: queueId || null,
        label: label || null,
        provider: "wacalls",
      });
      notifyCallOnce(
        `queue-ring-${callIdHit || ""}-1`,
        `Fila${label ? ` ${label}` : ""}: tocando — atenda para falar com o cliente.`
      );
      logTicketCallFromSoftphone({
        direction: "inbound",
        status: "started",
        callId: callIdHit,
        phone: phone || "",
        whatsappId: wa || whatsappId || undefined,
        queueId: queueId || undefined,
      });
    };

    const tick = async () => {
      const st = callStateRef.current?.status;
      // Em chamada / tocando: não mistura. pending_transfer continua o listen
      // para achar o park no canal certo após o toast da socket.
      if (
        st === "active" ||
        st === "ringing" ||
        st === "transferring" ||
        st === "incoming" ||
        st === "transfer_offer" ||
        st === "queue_ring" ||
        st === "queue_hold"
      ) {
        return;
      }
      // Softphone aberto pelo usuário para discar: NÃO reprocessa só URA
      // (fila/transferência ainda sobem no restante do tick).

      try {
        const { data } = await api.get("/call/incoming-listen", {
          params: whatsappId ? { preferredWhatsappId: whatsappId } : {},
        });
        if (cancelled) return;
        const hit = data?.hit;
        if (!hit?.whatsappId || !hit?.token) {
          // Durante URA: se não há hit novo, só re-injeta config ocasionalmente
          // (nunca se o agente está no discador)
          if (st === "ivr" && !agentPinnedOpenRef.current && !openRef.current) {
            const now = Date.now();
            if (now - lastIvrInjectAt < 3000) return;
            lastIvrInjectAt = now;
            const cs = callStateRef.current;
            if (!cs?.callId) return;
            const ch =
              (Array.isArray(channels) ? channels : []).find(
                (c) => Number(c.id) === Number(whatsappId)
              ) ||
              mfvList.find((c) => c.voiceIvrEnabled) ||
              null;
            const armed = await armIvrOnHub(cs.callId, whatsappId);
            const ivrCfg = buildIvrCfgFromAny(ch, armed, null);
            if (ivrCfg?.enabled) {
              injectIvrRetries({
                type: "inject-ivr-config",
                callId: cs.callId,
                peerNumber: cs.phone || "",
                peerName: cs.name || "",
                provider: "wacalls",
                voiceIvr: ivrCfg,
                quiet: true,
                force: true,
              });
            }
          }
          return;
        }

        const incomingIvr = hit.incoming?.voiceIvr || null;
        const isQueueHit =
          hit.kind === "incoming" &&
          (Boolean(hit.incoming?.queueHold) ||
            hit.incoming?.status === "queue_ring" ||
            incomingIvr?.phase === "done");

        // Pós-URA: abre softphone mesmo se o CRM ainda estava em status "ivr"
        if (isQueueHit) {
          const callIdHit = hit.incoming?.callId || "";
          const hitKey = `queue:${hit.whatsappId}:${callIdHit}`;
          if (lastMultiHitRef.current === hitKey) return;
          // Só canais liberados neste usuário (voiceUserIds da conexão)
          if (
            !agentMayRingIvrQueue(user, channels, {
              queueId: incomingIvr?.selectedQueueId || null,
              whatsappId: hit.whatsappId,
            })
          ) {
            lastMultiHitRef.current = hitKey;
            return;
          }
          lastMultiHitRef.current = hitKey;

          const hitChannelQ = mfvList.find(
            (c) => Number(c.id) === Number(hit.whatsappId)
          );
          // Só canais liberados (voiceUserIds); nunca usa token “vazado” de hit
          if (!hitChannelQ) {
            lastMultiHitRef.current = hitKey;
            return;
          }
          const sameCh =
            Number(hit.whatsappId) === Number(whatsappId) ||
            String(hit.token) === String(token);
          if (!sameCh && typeof onSwitchChannel === "function") {
            onSwitchChannel(hitChannelQ);
          }

          openAgentQueueRing({
            callIdHit,
            peer: hit.incoming?.peerDisplay || hit.incoming?.peerName || "",
            phone: hit.incoming?.peerNumber || "",
            peerName: hit.incoming?.peerName || "",
            queueId: incomingIvr?.selectedQueueId || null,
            label: null,
            whatsappId: hit.whatsappId,
          });
          return;
        }

        // Em URA e hit ainda é inbound com URA: só re-injeta e sai
        // Softphone aberto pelo atendente: não re-injetar (discador livre)
        if (st === "ivr" && hit.kind === "incoming") {
          if (agentPinnedOpenRef.current || openRef.current) {
            lastMultiHitRef.current =
              lastMultiHitRef.current ||
              `ivr-bg:${hit.whatsappId}:${hit.incoming?.callId || ""}`;
            return;
          }
          const now = Date.now();
          if (now - lastIvrInjectAt >= 3000) {
            lastIvrInjectAt = now;
            const cs = callStateRef.current;
            if (cs?.callId) {
              const ch =
                (Array.isArray(channels) ? channels : []).find(
                  (c) => Number(c.id) === Number(whatsappId)
                ) ||
                mfvList.find((c) => c.voiceIvrEnabled) ||
                null;
              const armed = await armIvrOnHub(cs.callId, whatsappId);
              const ivrCfg = buildIvrCfgFromAny(ch, armed, hit.incoming?.voiceIvr);
              if (ivrCfg?.enabled) {
                injectIvrRetries({
                  type: "inject-ivr-config",
                  callId: cs.callId,
                  peerNumber: cs.phone || hit.incoming?.peerNumber || "",
                  peerName: cs.name || hit.incoming?.peerName || "",
                  provider: "wacalls",
                  voiceIvr: ivrCfg,
                  quiet: true,
                  force: true,
                });
              }
            }
          }
          return;
        }

        const hitKey = `${hit.kind}:${hit.whatsappId}:${
          hit.incoming?.callId ||
          hit.transfer?.callId ||
          hit.phoneAlert?.callId ||
          ""
        }`;
        const sameHit = lastMultiHitRef.current === hitKey;

        const hitChannel = mfvList.find(
          (c) => Number(c.id) === Number(hit.whatsappId)
        );
        const rejects =
          String(hitChannel?.rejectCalls || "").toLowerCase() === "enabled";

        if (rejects && (hit.kind === "phone_alert" || hit.kind === "incoming")) {
          const phone =
            hit.incoming?.peerNumber ||
            hit.phoneAlert?.peerNumber ||
            hit.phoneAlert?.phone ||
            "";
          notifyAutoReject({
            phone,
            callId:
              hit.incoming?.callId ||
              hit.phoneAlert?.callId ||
              null,
            peerName:
              hit.incoming?.peerName || hit.phoneAlert?.peerName || null,
            waId: hit.whatsappId,
          });
          lastMultiHitRef.current = hitKey;
          return;
        }

        const sameChannel =
          Number(hit.whatsappId) === Number(whatsappId) ||
          String(hit.token) === String(token);

        // Só troca canal se o usuário tem permissão (lista já filtrada por voiceUserIds)
        if (!sameChannel && !hitChannel) {
          lastMultiHitRef.current = hitKey;
          return;
        }

        if (!sameChannel && hitChannel && typeof onSwitchChannel === "function") {
          onSwitchChannel(hitChannel);
        }

        if (hit.kind === "incoming" || hit.kind === "transfer") {
          const ch =
            hitChannel ||
            (Array.isArray(channels) ? channels : []).find(
              (c) => Number(c.id) === Number(hit.whatsappId)
            ) ||
            null;
          const callIdHit =
            hit.incoming?.callId || hit.transfer?.callId || "";
          let armed = null;
          if (hit.kind === "incoming" && callIdHit) {
            armed = await armIvrOnHub(callIdHit, hit.whatsappId);
          }
          const ivrCfg = buildIvrCfgFromAny(
            ch,
            armed,
            hit.incoming?.voiceIvr
          );
          // URA só se ainda não for fila
          const isIvr =
            hit.kind === "incoming" &&
            Boolean(ivrCfg?.enabled) &&
            !(
              Boolean(hit.incoming?.queueHold) ||
              hit.incoming?.status === "queue_ring" ||
              hit.incoming?.voiceIvr?.phase === "done"
            );
          const peer =
            hit.incoming?.peerDisplay ||
            hit.incoming?.peerName ||
            hit.incoming?.peerNumber ||
            hit.transfer?.peerDisplay ||
            "";
          const phone =
            hit.incoming?.peerNumber || hit.transfer?.peerNumber || "";

          // Com URA: softphone em background — SEMPRE injeta payload completo
          // (exceto se a URA já terminou e a fila já está tocando no agente)
          // (exceto se o usuário abriu o discador para outra ligação)
          if (isIvr) {
            if (shouldKeepSoftphoneForAgent(callIdHit)) {
              lastMultiHitRef.current = hitKey;
              return;
            }
            // Discador / softphone aberto pelo atendente: URA no server, não rouba a UI
            if (agentPinnedOpenRef.current || openRef.current) {
              lastMultiHitRef.current = hitKey;
              // Mantém arm no hub para server-ivr; não seta status ivr (preserva idle/discador)
              if (callIdHit) {
                void armIvrOnHub(callIdHit, hit.whatsappId);
              }
              return;
            }
            // Re-injeta a cada ~2,5s enquanto softphone não claimou (evita 1º poll perdido)
            if (sameHit && Date.now() - lastIvrInjectAt < 2500) {
              return;
            }
            lastMultiHitRef.current = hitKey;
            if (!hideSoftphoneForIvrOnly(callIdHit)) {
              return;
            }
            setCallState({
              status: "ivr",
              callId: callIdHit,
              peer,
              phone,
              name: hit.incoming?.peerName || "",
              seconds: 0,
              inCall: true,
              systemName: "Multiflow Voz",
            });
            lastIvrInjectAt = Date.now();
            injectIvrRetries({
              type: "inject-ivr-config",
              callId: callIdHit,
              peerNumber: phone,
              peerName: hit.incoming?.peerName || "",
              provider: "wacalls",
              voiceIvr: ivrCfg,
              quiet: true,
              force: true,
            });
            return;
          }

          if (sameHit) return;
          lastMultiHitRef.current = hitKey;

          setOpen(true);
          setMinimized(false);
          unlockVoiceAudio();
          maybeRequestVoiceNotifyOnGesture();
          ensureVoiceNotificationPermission().catch(() => {});

          setCallState({
            status:
              hit.kind === "transfer" ? "transfer_offer" : "incoming",
            callId: callIdHit,
            peer,
            phone,
            name: hit.incoming?.peerName || hit.transfer?.peerName || "",
            seconds: 0,
            inCall: true,
            systemName: "Multiflow Voz",
          });

          const focusSoftphone = () => {
            setOpen(true);
            setMinimized(false);
            try {
              window.focus();
            } catch {
              /* ignore */
            }
          };
          showVoiceDesktopNotification({
            kind: hit.kind === "transfer" ? "transfer" : "incoming",
            callId: callIdHit,
            name: hit.incoming?.peerName || hit.transfer?.peerName || "",
            phone,
            peer,
            onClick: focusSoftphone,
          });

          notifyCallOnce(
            callIdHit,
            hit.kind === "transfer"
              ? `Transferência: ${peer || phone}`
              : `Ligação recebida: ${peer || phone}`,
            { autoClose: 8000 }
          );
        }

        if (hit.kind === "phone_alert") {
          if (sameHit) return;
          lastMultiHitRef.current = hitKey;
          setOpen(true);
          setMinimized(false);
          unlockVoiceAudio();
          maybeRequestVoiceNotifyOnGesture();
          ensureVoiceNotificationPermission().catch(() => {});
          const phone =
            hit.phoneAlert?.peerNumber ||
            hit.phoneAlert?.phone ||
            "";
          const peer =
            hit.phoneAlert?.peerName ||
            hit.phoneAlert?.peerDisplay ||
            phone ||
            "WhatsApp";
          setCallState((s) => ({
            ...s,
            status: "incoming",
            callId: hit.phoneAlert?.callId || s.callId || "",
            peer,
            phone,
            name: hit.phoneAlert?.peerName || "",
            inCall: true,
            systemName: "Multiflow Voz",
          }));
          showVoiceDesktopNotification({
            kind: "incoming",
            callId: hit.phoneAlert?.callId || "",
            name: hit.phoneAlert?.peerName || "",
            phone,
            peer,
            onClick: () => {
              setOpen(true);
              setMinimized(false);
            },
          });
          notifyCallOnce(
            hit.phoneAlert?.callId || phone,
            `Chamada no celular / WhatsApp: ${peer}`,
            { autoClose: 12000 }
          );
        }
      } catch {
        /* silencioso — poll auxiliar */
      }
    };

    tick();
    const t = setInterval(tick, 1200);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [
    channels,
    token,
    whatsappId,
    onSwitchChannel,
    notifyAutoReject,
    notifyCallOnce,
    injectIvrRetries,
    armIvrOnHub,
    buildIvrCfgFromAny,
    shouldKeepSoftphoneForAgent,
    forceAgentSoftphoneOpen,
    cancelIvrInjectRetries,
    shouldBlockQueueRing,
    user,
  ]);

  // Toque no pai: principal no Multiflow (iframe cross-origin costuma bloquear autoplay).
  // Se o softphone conseguir tocar, ele manda "playing" e NÃO reiniciamos o mp3 no pai.
  useEffect(() => {
    if (callState.status === "idle") {
      toastOnceRef.current = "";
      lastMultiHitRef.current = "";
      // Residual do iframe religava o toque após Atender (idle limpa suppress)
      if (Date.now() >= ringQuietUntilRef.current) {
        suppressVoiceRing(false);
      }
    }
    const ringingStatuses = [
      "incoming",
      "transfer_offer",
      "pending_transfer",
    ];
    if (callState.status === "transferring") {
      // Origem: espera o destino — o toque fica só na tela de quem vai atender
      return undefined;
    }
    if (ringingStatuses.includes(callState.status)) {
      const cid = String(callState.callId || "");
      if (cid && answeredCallIdsRef.current.has(cid)) {
        stopAllVoiceSoundsAndSuppress();
        return undefined;
      }
      if (Date.now() < ringQuietUntilRef.current) {
        return undefined;
      }
      suppressVoiceRing(false);
      if (!isVoiceRingingPlaying()) {
        playRinging().catch(() => {});
      }
      return () => {
        const st = callStateRef.current?.status;
        if (
          st === "incoming" ||
          st === "transfer_offer" ||
          st === "pending_transfer" ||
          st === "transferring" ||
          st === "queue_ring" ||
          st === "queue_hold"
        ) {
          return;
        }
        stopAllVoiceSounds();
      };
    }
    if (callState.status === "ringing") {
      playCalling().catch(() => {});
      return () => stopAllVoiceSounds();
    }
    // Pós-URA: toque de fila (agente deve atender)
    if (
      callState.status === "queue_ring" ||
      callState.status === "queue_hold"
    ) {
      suppressVoiceRing(false);
      unlockVoiceAudio();
      let cancelled = false;
      const maybeRing = async () => {
        if (cancelled) return;
        if (
          callStateRef.current?.status !== "queue_ring" &&
          callStateRef.current?.status !== "queue_hold"
        ) {
          return;
        }
        const cid = callStateRef.current?.callId;
        if (cid && answeredCallIdsRef.current.has(String(cid))) return;
        // já tocando: não force-restart (causava engasgo a cada re-render)
        if (isVoiceRingingPlaying()) return;
        await playRinging().catch(() => {});
      };
      void (async () => {
        const first = await playRinging().catch(() => "blocked");
        // reforço só se autoplay bloqueou a 1ª tentativa
        if (first === "blocked" && !cancelled) {
          setTimeout(() => {
            void maybeRing();
          }, 400);
          setTimeout(() => {
            void maybeRing();
          }, 1200);
        }
      })();
      forceAgentSoftphoneOpen();
      return () => {
        cancelled = true;
        stopAllVoiceSounds();
      };
    }
    if (callState.status === "ivr") {
      // URA: sem toque de "atenda", mas NÃO fecha o popup do SO
      stopAllVoiceSounds();
      return undefined;
    }
    // active / idle / transferring: nunca deixar ring preso
    if (callState.status === "active") {
      stopAllVoiceSoundsAndSuppress();
    } else {
      stopAllVoiceSounds();
    }
    clearVoiceDesktopAlert();
    return undefined;
  }, [callState.status, forceAgentSoftphoneOpen]);
  // Popup do SO + título da aba (ligação humana / transferência / fila pós-URA)
  useEffect(() => {
    if (
      callState.status !== "incoming" &&
      callState.status !== "transfer_offer" &&
      callState.status !== "pending_transfer" &&
      callState.status !== "queue_ring" &&
      callState.status !== "queue_hold"
    ) {
      return;
    }
    const focusSoftphone = () => {
      forceAgentSoftphoneOpen();
    };
    showVoiceDesktopNotification({
      kind: callState.status === "transfer_offer" ? "transfer" : "incoming",
      callId: callState.callId || callState.phone || callState.peer || "",
      name: callState.name || "",
      phone: callState.phone || "",
      peer: callState.peer || "",
      onClick: focusSoftphone,
    });
  }, [
    callState.status,
    callState.callId,
    callState.name,
    callState.phone,
    callState.peer,
    forceAgentSoftphoneOpen,
  ]);

  // Limpa alertas nativos ao desmontar o widget
  useEffect(() => {
    return () => {
      clearVoiceDesktopAlert();
      stopAllVoiceSounds();
    };
  }, []);

  useEffect(() => {
    const onMessage = (ev) => {
      const data = ev?.data;
      if (!data || data.source !== "multiflow-voz-softphone") return;
      if (data.type !== "ringtone") return;
      const action = String(data.action || "");
      const kind = String(data.kind || "ring");
      // softphone pede toque no pai
      if (action === "start" || action === "blocked") {
        // Nunca reativar toque se já estamos em chamada atendida
        const stLive = callStateRef.current?.status;
        if (stLive === "active" || stLive === "ivr" || stLive === "transferring") {
          return;
        }
        if (
          callStateRef.current?.callId &&
          answeredCallIdsRef.current.has(String(callStateRef.current.callId))
        ) {
          return;
        }
        if (Date.now() < ringQuietUntilRef.current) {
          return;
        }
        if (
          callStateRef.current?.callId &&
          answeredCallIdsRef.current.has(String(callStateRef.current.callId))
        ) {
          return;
        }
        if (kind === "calling") {
          playCalling().catch(() => {});
        } else {
          suppressVoiceRing(false);
          // Não recomeça o loop se já está tocando (evita engasgo com multi-notify)
          if (!isVoiceRingingPlaying()) {
            playRinging().catch(() => {});
          }
        }
        return;
      }
      // Softphone tocou no iframe com sucesso: pai continua se já toca; sem restart
      if (action === "playing") {
        return;
      }
      if (action === "stop") {
        const st = callStateRef.current?.status;
        const answeredStop =
          data.answered === true ||
          data.reason === "answered" ||
          data.reason === "active";
        // Origem em transferring: iframe idle NÃO manda toque; destino para no answered
        if (
          (st === "transfer_offer" ||
            st === "pending_transfer") &&
          !answeredStop
        ) {
          return;
        }
        if (data.force === true || answeredStop) {
          stopAllVoiceSoundsAndSuppress();
          return;
        }
        if (
          st === "queue_ring" ||
          st === "queue_hold" ||
          st === "incoming"
        ) {
          return;
        }
        if (data.reason === "cleanup") {
          return;
        }
        stopAllVoiceSounds();
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      stopAllVoiceSounds();
    };
  }, []);

  // Fechar/declinar ticket de URA: encerra a ligação e não deixa o log reabrir card
  useEffect(() => {
    const onTicketClosed = (ev) => {
      const lastMsg = String(ev?.detail?.lastMessage || "");
      const isVoice = /liga[cç][aã]o|chamada de voz|ura/i.test(lastMsg);
      if (!isVoice) return;
      const st = callStateRef.current?.status;
      if (
        st !== "ivr" &&
        st !== "incoming" &&
        st !== "queue_ring" &&
        st !== "queue_hold"
      ) {
        return;
      }
      quietVoiceRing(8000);
      try {
        postToIframeRef.current?.({ type: "hangup" });
      } catch {
        /* ignore */
      }
      markCallEndedId(callStateRef.current?.callId);
      setCallState((s) => ({
        ...s,
        status: "idle",
        inCall: false,
        callId: "",
      }));
      callStateRef.current = {
        ...callStateRef.current,
        status: "idle",
        inCall: false,
        callId: "",
      };
    };
    window.addEventListener("mfv-voice-ticket-closed", onTicketClosed);
    return () =>
      window.removeEventListener("mfv-voice-ticket-closed", onTicketClosed);
  }, [quietVoiceRing, markCallEndedId]);

  useEffect(() => {
    const onTicketVoiceTransfer = (ev) => {
      const d = ev?.detail || {};
      const me = Number(currentUserId || user?.id || 0);
      const target = Number(d.targetUserId || 0);
      if (target && target === me) return;
      const st = callStateRef.current?.status;
      const live =
        st === "active" ||
        st === "ivr" ||
        st === "queue_hold" ||
        st === "queue_ring";
      if (!live || !target) return;
      postToIframeRef.current?.({
        type: "transfer-target",
        targetUserId: target,
        fromUserId: me,
        targetName: d.targetName || "",
      });
    };
    window.addEventListener("mfv-ticket-voice-transfer", onTicketVoiceTransfer);
    return () =>
      window.removeEventListener(
        "mfv-ticket-voice-transfer",
        onTicketVoiceTransfer
      );
  }, [currentUserId, user?.id]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(POS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed.x === "number" && typeof parsed.y === "number") {
          setPosition(parsed);
          positionRef.current = parsed;
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;

    const protectStatuses = new Set([
      "ivr",
      "active",
      "incoming",
      "ringing",
      "queue_ring",
      "queue_hold",
      "transfer_offer",
      "pending_transfer",
      "transferring",
    ]);
    const softphoneBusy = () => {
      const st = callStateRef.current?.status;
      return Boolean(
        protectStatuses.has(st) || callStateRef.current?.inCall
      );
    };

    // Preferência de canal (token) mudou, mas há mídia/URA — NÃO remonta o iframe
    // (isso derrubava ligações Oficial/ura e toques em que o agente nem atendeu).
    // Exceção: toque de fila/transferência/incoming — pode precisar amarrar o softphone à linha correta.
    if (
      frameTokenRef.current &&
      token !== frameTokenRef.current &&
      softphoneBusy()
    ) {
      const st = callStateRef.current?.status;
      const forceForRing =
        st === "queue_ring" ||
        st === "incoming" ||
        st === "transfer_offer" ||
        st === "pending_transfer";
      if (!forceForRing) {
        pendingFrameTokenRef.current = token;
        pendingFrameWhatsappIdRef.current = whatsappId;
        // eslint-disable-next-line no-console
        console.info(
          "[MultiflowVoz] troca de linha adiada até idle (chamada/URA em curso)"
        );
        return undefined;
      }
    }

    (async () => {
      const base = await resolveMultiflowVozBaseUrl();
      if (!base || cancelled) return;
      const { softphoneBase, vozHubBase } = resolveSoftphoneUiOrigin(base);
      const useWaId =
        token === frameTokenRef.current && frameWhatsappIdRef.current != null
          ? frameWhatsappIdRef.current
          : whatsappId;
      const ch =
        (Array.isArray(channels) ? channels : []).find(
          (c) => Number(c.id) === Number(useWaId)
        ) || null;
      const rejectCalls =
        String(ch?.rejectCalls || "").toLowerCase() === "enabled";
      const url = buildMultiflowVozSoftphoneUrl({
        base: softphoneBase,
        vozApiBase: vozHubBase || base,
        token,
        embed: true,
        hostChrome: true,
        rejectCalls,
        theme: uiTheme,
        agentQueueIds: getAgentQueueIds(user),
        agentUserId: user?.id || currentUserId || null,
      });
      // eslint-disable-next-line no-console
      console.info("[MultiflowVoz] softphone iframe:", url);
      frameTokenRef.current = token;
      frameWhatsappIdRef.current = whatsappId;
      pendingFrameTokenRef.current = null;
      pendingFrameWhatsappIdRef.current = null;
      setSrc(`${url}&_t=listen`);
    })();
    return () => {
      cancelled = true;
    };
    // theme: só no 1º load da URL; troca depois via postMessage set-theme
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, whatsappId, channels, user?.id, user?.queues]);

  // Quando fica idle, aplica troca de linha/canal adiada
  useEffect(() => {
    if (callState.status !== "idle" || callState.inCall) return;
    const pending = pendingFrameTokenRef.current;
    if (!pending || pending === frameTokenRef.current) return;
    frameTokenRef.current = null; // força rebuild no effect do token
    // re-dispara rebuild: simula troca “livre”
    pendingFrameTokenRef.current = null;
    const wa = pendingFrameWhatsappIdRef.current;
    pendingFrameWhatsappIdRef.current = null;
    // setSrc vazio + próximo ciclo: reusa effect via microtask
    void (async () => {
      const base = await resolveMultiflowVozBaseUrl();
      if (!base) return;
      const { softphoneBase, vozHubBase } = resolveSoftphoneUiOrigin(base);
      const ch =
        (Array.isArray(channels) ? channels : []).find(
          (c) => Number(c.id) === Number(wa)
        ) || null;
      const rejectCalls =
        String(ch?.rejectCalls || "").toLowerCase() === "enabled";
      const url = buildMultiflowVozSoftphoneUrl({
        base: softphoneBase,
        vozApiBase: vozHubBase || base,
        token: pending,
        embed: true,
        hostChrome: true,
        rejectCalls,
        theme: uiTheme,
        agentQueueIds: getAgentQueueIds(user),
        agentUserId: user?.id || currentUserId || null,
      });
      frameTokenRef.current = pending;
      frameWhatsappIdRef.current = wa;
      setSrc(`${url}&_t=listen`);
    })();
  }, [callState.status, callState.inCall, channels, user, currentUserId, uiTheme]);

  // Sincroniza tema sem remount do iframe
  useEffect(() => {
    if (!src) return;
    postToIframeRef.current?.({ type: "set-theme", theme: uiTheme });
  }, [uiTheme, src]);

  const openWith = useCallback(
    async ({ phone = "", name = "", startIfReady = false } = {}) => {
      if (!token) return;
      unlockVoiceAudio();
      maybeRequestVoiceNotifyOnGesture();
      ensureVoiceNotificationPermission().catch(() => {});
      agentPinnedOpenRef.current = true;
      // Cancela re-injeções de URA para o softphone (URA segue server-side se claim server-ivr)
      cancelIvrInjectRetries();
      const base = await resolveMultiflowVozBaseUrl();
      if (!base) {
        // eslint-disable-next-line no-alert
        alert(
          "URL do Multiflow Voz não configurada. O usuário master deve definir em Configurações → Multiflow Voz."
        );
        return;
      }
      const { softphoneBase, vozHubBase } = resolveSoftphoneUiOrigin(base);
      const ch =
        (Array.isArray(channels) ? channels : []).find(
          (c) => Number(c.id) === Number(whatsappId)
        ) || null;
      const url = buildMultiflowVozSoftphoneUrl({
        base: softphoneBase,
        vozApiBase: vozHubBase || base,
        token,
        phone,
        name,
        embed: true,
        startIfReady,
        hostChrome: true,
        rejectCalls:
          String(ch?.rejectCalls || "").toLowerCase() === "enabled",
        theme: uiTheme,
        agentQueueIds: getAgentQueueIds(user),
        agentUserId: user?.id || currentUserId || null,
      });
      // Remonta se discador (phone) ou se estava em URA — libera claim do softphone
      // para a URA receptiva seguir no server-ivr enquanto o atendente discar.
      const wasIvr =
        callStateRef.current?.status === "ivr" ||
        callStateRef.current?.status === "queue_hold";
      if (phone || startIfReady || !src || wasIvr || token !== frameTokenRef.current) {
        // openWith é escolha consciente de linha/discar — não adia remount
        pendingFrameTokenRef.current = null;
        pendingFrameWhatsappIdRef.current = null;
        frameTokenRef.current = token;
        frameWhatsappIdRef.current = whatsappId;
        setSrc(`${url}&_t=${Date.now()}`);
      }
      setCallState({
        status: "idle",
        phone: String(phone || "").replace(/\D/g, ""),
        name: name || "",
        peer: name || String(phone || "").replace(/\D/g, ""),
        callId: "",
        seconds: 0,
        inCall: false,
        systemName: "Multiflow Voz",
      });
      setMinimized(false);
      setOpen(true);
      // Foca o iframe para o teclado físico digitar o número
      requestAnimationFrame(() => {
        setTimeout(() => {
          postToIframeRef.current?.({ type: "ui-surface", active: true });
          iframeRef.current?.focus?.();
          iframeRef.current?.contentWindow?.postMessage(
            { target: "multiflow-voz-softphone", type: "focus" },
            "*"
          );
        }, 120);
      });
    },
    [token, whatsappId, channels, src, uiTheme, cancelIvrInjectRetries, user]
  );
  openWithRef.current = openWith;

  // Comunica ao softphone se o discador está realmente visível
  // (evita autofocus/Enter no iframe fantasma 1×1).
  // Com busca de contatos (CRM) aberta: size ok, mas focus/teclado do iframe OFF.
  useEffect(() => {
    const surfaceActive = Boolean(open && !minimized);
    const focusAllowed = surfaceActive && !dialerContactOpen;
    const send = () =>
      postToIframeRef.current?.({
        type: "ui-surface",
        active: surfaceActive,
        focus: focusAllowed,
      });
    send();
    if (!focusAllowed) {
      try {
        const ae = document.activeElement;
        if (
          ae &&
          iframeRef.current &&
          (ae === iframeRef.current || iframeRef.current.contains?.(ae))
        ) {
          ae.blur?.();
        }
        iframeRef.current?.blur?.();
      } catch {
        /* ignore */
      }
    } else {
      // Retry: iframe pode carregar um tick depois
      requestAnimationFrame(() => {
        setTimeout(send, 50);
        setTimeout(send, 300);
      });
    }
  }, [open, minimized, src, dialerContactOpen]);

  // Painel fechado / mini: devolve o foco ao CRM (evita Enter no iframe roubar discador)
  useEffect(() => {
    if (open && !minimized) return undefined;
    try {
      const ae = document.activeElement;
      if (ae && iframeRef.current && (ae === iframeRef.current || iframeRef.current.contains?.(ae))) {
        ae.blur?.();
      }
      iframeRef.current?.blur?.();
    } catch {
      /* ignore */
    }
    return undefined;
  }, [open, minimized]);

  useEffect(() => {
    const handler = async (e) => {
      const detail = e?.detail || {};
      const action = detail.action || "open";

      if (action === "close") {
        agentPinnedOpenRef.current = false;
        setOpen(false);
        setMinimized(false);
        try {
          iframeRef.current?.blur?.();
        } catch {
          /* ignore */
        }
        return;
      }

      if (action === "toggle") {
        if (openRef.current && agentPinnedOpenRef.current) {
          // Fecha só se não estiver em toque de fila/recebida
          if (!isAgentFacingStatus(callStateRef.current?.status)) {
            agentPinnedOpenRef.current = false;
            setOpen(false);
            setMinimized(false);
            try {
              iframeRef.current?.blur?.();
            } catch {
              /* ignore */
            }
            return;
          }
        }
        if (openRef.current && !agentPinnedOpenRef.current) {
          // Painel que era só background de URA: abrir de verdade = pin
          await openWith(detail);
          return;
        }
        if (openRef.current) {
          agentPinnedOpenRef.current = false;
          setOpen(false);
          setMinimized(false);
          return;
        }
        await openWith(detail);
        return;
      }

      await openWith(detail);
    };

    window.addEventListener(MFV_SOFTPHONE_EVENT, handler);
    return () => window.removeEventListener(MFV_SOFTPHONE_EVENT, handler);
  }, [openWith]);

  useEffect(() => {
    const onMessage = (ev) => {
      const data = ev?.data;
      if (!data || data.source !== "multiflow-voz-softphone") return;

      const injectIvrToSoftphone = (opts = {}) => {
        const ch =
          (Array.isArray(channels) ? channels : []).find(
            (c) =>
              Number(c.id) === Number(opts.whatsappId || whatsappId) ||
              String(c.wavoip || "") === String(opts.token || token || "")
          ) ||
          (Array.isArray(channels) ? channels : []).find((c) =>
            c?.voiceIvrEnabled
          ) ||
          null;
        const base =
          String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") ||
          "";
        const voiceIvr = buildChannelVoiceIvrForSoftphone(ch, base);
        if (!voiceIvr) return false;
        injectIvrRetries({
          type: "inject-ivr-config",
          callId: opts.callId || "",
          peerNumber: opts.peerNumber || "",
          peerName: opts.peerName || "",
          provider: opts.provider || "wacalls",
          voiceIvr,
          quiet: Boolean(opts.quiet),
        });
        if (opts.callId) {
          armIvrOnHub(opts.callId, opts.whatsappId || whatsappId);
        }
        return true;
      };

      if (data.type === "request-ivr-config") {
        injectIvrToSoftphone({
          callId: data.callId,
          peerNumber: data.peerNumber,
          peerName: data.peerName,
          provider: data.provider,
          whatsappId,
          token,
          quiet: Boolean(data.quiet),
        });
        return;
      }

      if (data.type === "ivr-speech") {
        (async () => {
          try {
            const { data: resp } = await api.post(
              "/call/ivr-speech",
              {
                audioBase64: data.audioBase64,
                mimeType: data.mimeType || "audio/wav",
                phone: data.phone || "",
                callId: data.callId || "",
                whatsappId: data.whatsappId || whatsappId || undefined,
                applyRoute: true,
              },
              { timeout: 60000 }
            );
            postToIframeRef.current?.({
              type: "ivr-speech-result",
              id: data.id,
              result: resp || {},
            });
          } catch (err) {
            postToIframeRef.current?.({
              type: "ivr-speech-result",
              id: data.id,
              result: {
                error:
                  err?.response?.data?.error ||
                  err?.message ||
                  "Falha na URA por voz",
                retry: true,
              },
            });
          }
        })();
        return;
      }

      if (data.type === "ivr-speech-failed") {
        const msg = String(data.message || "URA não entendeu o setor").trim();
        if (msg) toastError(msg);
        return;
      }

      if (data.type === "fetch-public-audio") {
        (async () => {
          const reply = (payload) => {
            postToIframeRef.current?.({
              type: "fetch-audio-result",
              id: data.id,
              ...payload,
            });
          };
          try {
            let url = String(data.url || "").trim();
            if (!url) {
              reply({ ok: false });
              return;
            }
            const base = String(
              process.env.REACT_APP_BACKEND_URL || ""
            ).replace(/\/$/, "");
            if (url.startsWith("/") && base) {
              url = `${base}${url}`;
            }
            const r = await fetch(url, { credentials: "include" });
            if (!r.ok) {
              reply({ ok: false, status: r.status });
              return;
            }
            const buf = await r.arrayBuffer();
            const bytes = new Uint8Array(buf);
            let binary = "";
            const chunk = 0x8000;
            for (let i = 0; i < bytes.length; i += chunk) {
              binary += String.fromCharCode.apply(
                null,
                bytes.subarray(i, i + chunk)
              );
            }
            reply({ ok: true, audioBase64: btoa(binary) });
          } catch (err) {
            reply({ ok: false, error: err?.message || "fetch fail" });
          }
        })();
        return;
      }

      if (data.type === "close") {
        setOpen(false);
        setMinimized(false);
        return;
      }
      if (data.type === "minimize") {
        // Em toque de fila, não deixa ficar só no mini-badge
        const st = callStateRef.current?.status;
        if (st === "queue_ring" || st === "queue_hold") {
          forceAgentSoftphoneOpen();
          return;
        }
        setMinimized(true);
        return;
      }
      if (data.type === "dialer-pick-contact") {
        setOpen(true);
        setMinimized(false);
        setDialerContactOpen(true);
        return;
      }
      if (data.type === "error") {
        const message = String(data.message || "").trim();
        if (message) {
          toastError(message);
          setOpen(true);
          setMinimized(false);
        }
        return;
      }
      if (data.type === "transfer-pick") {
        (async () => {
          try {
            setTransferSearch("");
            const fromId = Number(currentUserId || user?.id || 0);
            const loadTargets = async (params = {}) => {
              const { data: resp } = await api.get("/call/voice-targets", {
                params,
              });
              return Array.isArray(resp?.users) ? resp.users : [];
            };

            // 1) restrição da conexão (voiceUserIds)  2) fallback company-wide
            let users = await loadTargets(
              whatsappId ? { whatsappId } : {}
            );
            let expanded = false;
            if (!users.length && whatsappId) {
              users = await loadTargets({});
              expanded = users.length > 0;
            }
            // Nunca lista o próprio usuário como destino
            users = users.filter((u) => Number(u.id) !== fromId);

            setTransferPickMeta({
              callId: data.callId,
              phone: data.phone || "",
              peerName: data.peerName || "",
            });

            if (!users.length) {
              // NÃO inicia transferência broadcast — evita cair em qualquer softphone (ex.: Denise)
              postToIframeRef.current?.({ type: "transfer-cancel" });
              setTransferPickMeta(null);
              setTransferTargets(null);
              toast.error(
                "Nenhum usuário disponível para transferir. Libere mais usuários de voz na conexão ou cadastre outros atendentes."
              );
              setOpen(true);
              setMinimized(false);
              return;
            }

            if (expanded) {
              toast.info(
                "Nenhum destino liberado nesta conexão — mostrando todos os usuários da empresa.",
                { autoClose: 6000 }
              );
            }

            // Sempre exibe seletor (mesmo com 1 usuário) — nunca auto-envia
            setTransferTargets(users);
            setOpen(true);
            setMinimized(false);
          } catch (err) {
            toastError(err);
            postToIframeRef.current?.({ type: "transfer-cancel" });
            setTransferPickMeta(null);
            setTransferTargets(null);
          }
        })();
        return;
      }
      if (data.type === "voice-transfer") {
        try {
          const socket = socketConnection({ user });
          socket.emit("voice-transfer", {
            callId: data.callId,
            targetUserId: data.targetUserId,
            fromUserId: data.fromUserId || currentUserId || user?.id,
            phone: data.phone,
            peerName: data.peerName,
            peerLid: data.peerLid,
            // Destino precisa do canal certo (ex.: 8802). Sem isso o softphone
            // abre no token atual (Oficial7700) e não vê o park da conexão.
            whatsappId: whatsappId || null,
            token: token || null,
            provider: data.provider || null,
          });
        } catch (err) {
          console.error("[MultiflowVoz] voice-transfer emit:", err);
        }
        setTransferWaiting((prev) => ({
          targetName: data.targetName || prev?.targetName || "",
          peerName: data.peerName || prev?.peerName || "",
          phone: data.phone || prev?.phone || "",
          callId: data.callId || prev?.callId || "",
        }));
        setOpen(true);
        setMinimized(false);
        setCallState((s) => ({
          ...s,
          status: "transferring",
          transferring: true,
          transferTargetName:
            data.targetName || s.transferTargetName || "",
          inCall: true,
          callId: data.callId || s.callId || "",
          phone: data.phone || s.phone,
          name: data.peerName || s.name,
          peer: data.peerName || data.phone || s.peer,
        }));
        return;
      }
      if (data.type === "transfer-waiting-done") {
        setTransferWaiting(null);
        if (data.reason !== "cancelled") {
          quietVoiceRing(8000);
        }
        setCallState((s) => ({
          ...s,
          transferring: false,
          transferTargetName: "",
          status:
            s.status === "transferring"
              ? data.reason === "cancelled"
                ? "active"
                : "idle"
              : s.status,
          inCall: data.reason === "cancelled" ? true : s.inCall,
        }));
        return;
      }
      if (data.type === "transfer-offer") {
        const offerId = String(data.callId || "");
        // Mesmo callId da ligação original: NÃO tratar como "já atendida"
        // (voice-answered do originador envenenava o destino — Denise sem Atender).
        if (offerId) clearCallAnswered(offerId);
        if (callStateRef.current?.status === "active") {
          return;
        }
        setTransferWaiting(null);
        setOpen(true);
        setMinimized(false);
        setCallState((s) => {
          // Evita re-render/status se já está tocando a mesma transferência
          if (
            (s.status === "transfer_offer" || s.status === "pending_transfer") &&
            data.callId &&
            String(s.callId) === String(data.callId)
          ) {
            return s;
          }
          return {
            ...s,
            status: "transfer_offer",
            inCall: true,
            callId: data.callId || s.callId || "",
            phone: data.peerNumber || data.phone || s.phone,
            name: data.peerName || data.peer || s.name,
            peer: data.peerName || data.peer || data.phone || s.peer,
          };
        });
        // Toque só se ainda não estiver a tocar (socket + poll não podem restartar)
        if (
          callStateRef.current?.status !== "active" &&
          !isVoiceRingingPlaying()
        ) {
          suppressVoiceRing(false);
          playRinging().catch(() => {});
        }
        const toastKey = `transfer-offer-${data.callId || ""}`;
        if (toastOnceRef.current !== toastKey) {
          toastOnceRef.current = toastKey;
          toast.info(
            `Transferência de ligação${
              data.peerName || data.peer
                ? ` (${data.peerName || data.peer})`
                : ""
            }. Abra o softphone para atender.`,
            { autoClose: 12000, toastId: `mfv-${toastKey}` }
          );
        }
        return;
      }
      if (data.type === "call-started") {
        markCallAnswered(data.callId || callStateRef.current?.callId);
        suppressVoiceRing(true);
        stopAllVoiceSoundsAndSuppress();
        callStateRef.current = {
          ...callStateRef.current,
          status: "active",
          inCall: true,
          callId: data.callId || callStateRef.current?.callId || "",
        };
        setCallState((s) => ({
          ...s,
          status: "active",
          inCall: true,
          transferring: false,
          transferTargetName: "",
          phone: data.phone || s.phone,
          name: data.peerName || s.name,
          peer: data.peerName || data.phone || s.peer,
          callId: data.callId || s.callId,
        }));
        setTransferWaiting(null);
        // Avisa as OUTRAS telas (fila/transfer) para parar o toque
        try {
          const socket = socketConnection({ user });
          socket.emit("voice-answered", {
            callId: data.callId || callStateRef.current?.callId,
            fromUserId: currentUserId || user?.id,
            queueId: data.queueId || null,
            phone: data.phone || "",
          });
        } catch {
          /* ignore */
        }
        // Atendente assumiu a mídia → open + user + fila
        logTicketCallFromSoftphone({
          direction: data.direction === "outbound" ? "outbound" : "inbound",
          status: "answered",
          callId: data.callId,
          phone: data.phone || "",
          peerLid: data.peerLid || "",
          whatsappId: whatsappId || undefined,
          queueId: data.queueId || undefined,
        });
        return;
      }
      if (data.type === "transfer-returned") {
        setTransferWaiting(null);
        toast.info(
          "Ninguém atendeu a transferência — reassumindo a ligação…",
          { autoClose: 8000 }
        );
        setOpen(true);
        setMinimized(false);
        return;
      }
      if (data.type === "transfer-rejected") {
        // Destino recusou: limpa UI de oferta no softphone pai (sem re-toque)
        stopAllVoiceSounds();
        clearVoiceDesktopAlert();
        setCallState((s) => {
          if (
            s.status !== "transfer_offer" &&
            s.status !== "pending_transfer"
          ) {
            return s;
          }
          if (
            data.callId &&
            s.callId &&
            String(s.callId) !== String(data.callId)
          ) {
            return s;
          }
          return {
            ...s,
            status: "idle",
            inCall: false,
            callId: "",
            transferring: false,
            transferTargetName: "",
          };
        });
        callStateRef.current = {
          ...callStateRef.current,
          status: "idle",
          inCall: false,
          callId: "",
        };
        return;
      }
      if (data.type === "phone-alert") {
        const callId = String(data.callId || "");
        if (callId) {
          if (phoneAlertedRef.current.has(callId)) return;
          phoneAlertedRef.current.add(callId);
          if (phoneAlertedRef.current.size > 40) {
            const first = phoneAlertedRef.current.values().next().value;
            phoneAlertedRef.current.delete(first);
          }
        }
        const phone = String(data.peerNumber || data.phone || "").replace(
          /\D/g,
          ""
        );
        const name = String(data.peerName || data.name || "").trim();

        // Baileys + mfv: softphone ainda gera alerta COEX — se a conexão
        // recusa chamadas, não pisca widget e reforça o envio da msg.
        if (channelRejectsCalls(whatsappId)) {
          notifyAutoReject({
            phone,
            callId: callId || null,
            peerName: name || null,
            lid: data.peerLid || null,
            waId: whatsappId,
          });
          return;
        }

        const who = name
          ? phone
            ? `${name} (${phone})`
            : name
          : phone || "contato";
        toast.info(
          `📞 Ligação no celular de ${who}. Atenda no WhatsApp Business ou clique para retornar pelo softphone.`,
          {
            autoClose: 14000,
            onClick: () => {
              openWithRef.current?.({
                phone,
                name,
                startIfReady: false,
              });
            },
          }
        );
        setOpen(true);
        setMinimized(false);
        setCallState((s) => ({
          ...s,
          status: "idle",
          phone: phone || s.phone,
          name: name || s.name,
          peer: name || phone || s.peer,
          inCall: false,
        }));
        if (phone || name) {
          postToIframeRef.current?.({
            type: "set-phone",
            phone,
            name,
          });
        }
        return;
      }
      if (data.type === "call-ended") {
        const endStatus = data.status || "ended";
        // Encerra UI/toque da fila (antes o modal ficava preso em queue_ring)
        markCallEndedId(data.callId);
        forceSoftphoneIdle(data.callId);
        // Outbound iniciado pelo ticket já registra o fim em TicketActionButtons;
        // aqui cobrimos inbound + outbound pelo discador do softphone.
        if (rememberLoggedCall(data.callId, endStatus)) return;
        logTicketCallFromSoftphone({
          direction: data.direction,
          status: endStatus,
          callId: data.callId,
          phone: data.phone,
          peerLid: data.peerLid,
          whatsappId: whatsappId || undefined,
        });
        return;
      }
      if (data.type === "ivr-queue-ring") {
        const stNow = callStateRef.current?.status;
        if (
          stNow === "transferring" ||
          stNow === "transfer_offer" ||
          stNow === "pending_transfer" ||
          stNow === "active"
        ) {
          return;
        }
        // Guard ANTES de tocar — residual das rodadas pós-Atender rearmava o ring
        if (shouldBlockQueueRing(data.callId)) {
          stopAllVoiceSoundsAndSuppress();
          return;
        }
        // Fila do setor + conexão liberada (voiceUserIds)
        if (
          !agentMayRingIvrQueue(user, channels, {
            queueId: data.queueId,
            whatsappId: data.whatsappId || whatsappId,
          })
        ) {
          return;
        }
        cancelIvrInjectRetries();
        suppressVoiceRing(false);
        unlockVoiceAudio();
        // re-tenta toque só se a 1ª falhar (autoplay)
        void playRinging().then((r) => {
          if (r === "blocked" && !shouldBlockQueueRing(data.callId)) {
            if (
              agentMayRingIvrQueue(user, channels, {
                queueId: data.queueId,
                whatsappId: data.whatsappId || whatsappId,
              })
            ) {
              setTimeout(() => {
                if (!shouldBlockQueueRing(data.callId) && !isVoiceRingingPlaying()) {
                  playRinging().catch(() => {});
                }
              }, 400);
            }
          }
        });
        forceAgentSoftphoneOpen();
        requestAnimationFrame(() => forceAgentSoftphoneOpen());
        const nextQueue = {
          status: "queue_ring",
          callId: data.callId || "",
          peer: data.peerName || data.phone || data.peerNumber || "",
          phone: data.phone || data.peerNumber || "",
          name: data.peerName || "",
          seconds: 0,
          inCall: true,
          systemName: "Multiflow Voz",
          queueId: data.queueId || null,
          queueLabel: data.label || null,
          remainingSec: data.remainingSec || data.maxWaitSec || null,
          round: data.round || 1,
        };
        setCallState(nextQueue);
        callStateRef.current = { ...callStateRef.current, ...nextQueue };
        // Atualiza histórico (status fila) — socket refresh no backend
        logTicketCallFromSoftphone({
          direction: "inbound",
          status: "started",
          callId: data.callId,
          phone: data.phone || data.peerNumber || "",
          peerLid: data.peerLid || "",
          whatsappId: whatsappId || undefined,
          queueId: data.queueId || undefined,
        });
        notifyCallOnce(
          `queue-ring-${data.callId || ""}-${data.round || 1}`,
          `Fila${data.label ? ` ${data.label}` : ""}: tocando — atenda para falar com o cliente.`
        );
        return;
      }
      if (data.type === "ivr-queue-answered") {
        markCallAnswered(data.callId || callStateRef.current?.callId);
        quietVoiceRing(8000);
        callStateRef.current = {
          ...callStateRef.current,
          status: "active",
          inCall: true,
          callId: data.callId || callStateRef.current?.callId,
        };
        setCallState((s) => ({
          ...s,
          status: "active",
          inCall: true,
          callId: data.callId || s.callId,
        }));
        try {
          const socket = socketConnection({ user });
          socket.emit("voice-answered", {
            callId: data.callId,
            fromUserId: currentUserId || user?.id,
            queueId: data.queueId || null,
          });
        } catch {
          /* ignore */
        }
        return;
      }
      if (data.type === "ivr-queue-timeout" || data.type === "ivr-queue-goodbye") {
        stopAllVoiceSounds();
        clearVoiceDesktopAlert();
        if (data.type === "ivr-queue-timeout") {
          notifyCallOnce(
            `queue-timeout-${data.callId || ""}`,
            "URA: tempo esgotado na fila — ligação encerrada."
          );
        }
        setCallState({
          status: "idle",
          callId: "",
          peer: "",
          phone: "",
          name: "",
          seconds: 0,
          inCall: false,
          systemName: "Multiflow Voz",
          queueId: null,
          queueLabel: null,
        });
        callStateRef.current = {
          status: "idle",
          callId: "",
          inCall: false,
          queueId: null,
          queueLabel: null,
        };
        return;
      }
      if (data.type === "ivr-queue-hold-audio") {
        // Mantém popup; toque para até a próxima rodada
        stopAllVoiceSounds();
        return;
      }
      if (data.type === "ivr-running") {
        // URA em background — se o atendente abriu o discador, não rouba a UI
        if (agentPinnedOpenRef.current || openRef.current) {
          logTicketCallFromSoftphone({
            direction: "inbound",
            status: "started",
            callId: data.callId,
            phone: data.peerNumber || data.peer || "",
            peerLid: data.peerLid || "",
            whatsappId: whatsappId || undefined,
          });
          return;
        }
        if (!hideSoftphoneForIvrOnly(data.callId)) {
          return;
        }
        setCallState({
          status: "ivr",
          callId: data.callId || "",
          peer: data.peer || data.peerName || data.peerNumber || "",
          phone: data.peerNumber || data.peer || "",
          name: data.peerName || "",
          seconds: 0,
          inCall: true,
          systemName: "Multiflow Voz",
        });
        // Ticket imediato: pending, sem dono (URA)
        logTicketCallFromSoftphone({
          direction: "inbound",
          status: "started",
          callId: data.callId,
          phone: data.peerNumber || data.peer || "",
          peerLid: data.peerLid || "",
          whatsappId: whatsappId || undefined,
        });
        return;
      }
      if (data.type === "ivr-done") {
        // Pós-URA: cliente na FILA — não marcar active/answered (amarrava o Admin)
        const qid = data.queueId || null;
        setCallState((s) => ({
          ...s,
          status: qid ? "queue_ring" : s.status === "ivr" ? "queue_ring" : s.status,
          inCall: true,
          callId: data.callId || s.callId,
          phone: data.phone || s.phone,
          queueId: qid || s.queueId || null,
          queueLabel: data.label || s.queueLabel || null,
        }));
        callStateRef.current = {
          ...callStateRef.current,
          status: qid ? "queue_ring" : callStateRef.current?.status,
          inCall: true,
          callId: data.callId || callStateRef.current?.callId,
          queueId: qid || callStateRef.current?.queueId || null,
        };
        if (qid) {
          forceAgentSoftphoneOpen();
          notifyCallOnce(
            `ivr-done-${data.callId || ""}`,
            `URA: cliente na fila${data.label ? ` (${data.label})` : ""}.`
          );
        }
        logTicketCallFromSoftphone({
          direction: "inbound",
          status: "started",
          callId: data.callId,
          phone: data.phone || "",
          whatsappId: whatsappId || undefined,
          queueId: qid || undefined,
        });
        return;
      }
      if (data.type === "incoming") {
        // Mesmo com Baileys rejeitando, o softphone mfv_ pode mostrar "recebida".
        if (channelRejectsCalls(whatsappId)) {
          notifyAutoReject({
            phone: data.peerNumber || data.peer || "",
            callId: data.callId || null,
            peerName: data.peerName || null,
            lid: data.peerLid || null,
            waId: whatsappId,
          });
          postToIframeRef.current?.({ type: "hangup" });
          return;
        }
        unlockVoiceAudio();
        maybeRequestVoiceNotifyOnGesture();
        ensureVoiceNotificationPermission().catch(() => {});

        const ch =
          (Array.isArray(channels) ? channels : []).find(
            (c) => Number(c.id) === Number(whatsappId)
          ) || null;
        const ivrCfg = buildChannelVoiceIvrForSoftphone(
          ch,
          String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")
        );

        // Se a conexão tem URA: não abre painel para o atendente
        // (exceto se a fila já está tocando pós-URA / discador aberto)
        if ((ivrCfg || data.willIvr) && !shouldKeepSoftphoneForAgent(data.callId)) {
          if (agentPinnedOpenRef.current) {
            if (data.callId) armIvrOnHub(data.callId, whatsappId);
            return;
          }
          if (!hideSoftphoneForIvrOnly(data.callId)) {
            return;
          }
          setCallState({
            status: "ivr",
            callId: data.callId || "",
            peer: data.peer || data.peerName || data.peerNumber || "",
            phone: data.peerNumber || data.peer || "",
            name: data.peerName || "",
            seconds: 0,
            inCall: true,
            systemName: "Multiflow Voz",
          });
          if (data.callId) armIvrOnHub(data.callId, whatsappId);
          if (ivrCfg) {
            injectIvrRetries({
              type: "inject-ivr-config",
              callId: data.callId || "",
              peerNumber: data.peerNumber || "",
              peerName: data.peerName || "",
              provider: "wacalls",
              voiceIvr: ivrCfg,
              quiet: true,
            });
          }
          return;
        }

        setOpen(true);
        setMinimized(false);

        setCallState({
          status: "incoming",
          callId: data.callId || "",
          peer: data.peer || data.peerName || data.peerNumber || "",
          phone: data.peerNumber || data.peer || "",
          name: data.peerName || "",
          seconds: 0,
          inCall: true,
          systemName: "Multiflow Voz",
        });
        showVoiceDesktopNotification({
          kind: "incoming",
          callId: data.callId || "",
          name: data.peerName || "",
          phone: data.peerNumber || "",
          peer: data.peer || data.peerName || "",
          onClick: () => {
            setOpen(true);
            setMinimized(false);
            try {
              window.focus();
            } catch {
              /* ignore */
            }
          },
        });
        notifyCallOnce(
          data.callId || data.peerNumber,
          `Ligação recebida: ${
            data.peerName || data.peerNumber || "WhatsApp"
          }`
        );
        resolveMultiflowContactLabel({
          peer: data.peer,
          peerName: data.peerName,
          peerLid: data.peerLid,
          phone: data.peerNumber || data.peer,
        }).then((resolved) => {
          setCallState((s) => {
            if (s.status !== "incoming") return s;
            return {
              ...s,
              name: resolved.name || data.peerName || "",
              phone: resolved.phone || data.peerNumber || data.peer || s.phone,
              peer: resolved.peer || data.peer || data.peerName || s.peer,
            };
          });
          if (resolved.name || resolved.phone) {
            postToIframeRef.current?.({
              type: "resolved-contact",
              name: resolved.name || "",
              phone: resolved.phone || "",
              peer: resolved.peer || "",
            });
          }
        });
        return;
      }
      if (data.type === "state") {
        const rawStatus = data.status || "idle";
        // Chamada já encerrada pela Meta/CRM: não deixa iframe rearmar timer em "ringing"
        const dataCid = String(data.callId || "").trim();
        if (
          dataCid &&
          endedCallIdsRef.current.has(dataCid) &&
          rawStatus !== "idle"
        ) {
          return;
        }
        let nextStatus =
          rawStatus === "queue_hold" ? "queue_ring" : rawStatus;
        const prev = callStateRef.current;
        // Já atendido: NÃO rebaixar active com residual de fila/URA do iframe
        if (
          prev?.status === "active" ||
          (data.callId &&
            answeredCallIdsRef.current.has(String(data.callId))) ||
          (prev?.callId &&
            answeredCallIdsRef.current.has(String(prev.callId)))
        ) {
          if (
            nextStatus === "queue_ring" ||
            nextStatus === "queue_hold" ||
            nextStatus === "ivr" ||
            nextStatus === "incoming"
          ) {
            const sameOrEmpty =
              !data.callId ||
              !prev?.callId ||
              String(data.callId) === String(prev.callId);
            if (sameOrEmpty) {
              nextStatus = "active";
            }
          }
        }
        // Softphone pode reenviar "ivr" residual após a fila começar.
        // NÃO limpar queue_ring com "idle" residual: isso matava o toque e o UI
        // se o iframe ainda não recebeu server-ivr-queue-ring. O fim real
        // chega por call-ended / voice-call-ended / ivr-queue-timeout.
        if (
          prev &&
          (prev.status === "queue_ring" || prev.status === "queue_hold") &&
          (nextStatus === "ivr" || nextStatus === "idle") &&
          nextStatus !== "active"
        ) {
          const sameOrEmpty =
            !data.callId ||
            !prev.callId ||
            String(data.callId) === String(prev.callId);
          // idle genuíno se call-ended já marcou, ou callId mudou para vazio com fim
          if (
            sameOrEmpty &&
            nextStatus === "idle" &&
            dataCid &&
            endedCallIdsRef.current.has(dataCid)
          ) {
            // deixa idle
          } else if (sameOrEmpty && nextStatus !== "idle") {
            nextStatus = "queue_ring";
          } else if (
            sameOrEmpty &&
            nextStatus === "idle" &&
            !dataCid &&
            !endedCallIdsRef.current.has(String(prev.callId || ""))
          ) {
            // residual idle sem callId — não matar fila ainda
            nextStatus = "queue_ring";
          }
        }
        // Fila pós-URA: só com queueId da MESMA call (não herda fila do call anterior!)
        if (nextStatus === "queue_ring" || nextStatus === "queue_hold") {
          const sameCall =
            dataCid &&
            prev?.callId &&
            String(dataCid) === String(prev.callId);
          const qid =
            data.queueId != null
              ? data.queueId
              : sameCall
                ? prev?.queueId || callStateRef.current?.queueId
                : null;
          if (
            !agentMayRingIvrQueue(user, channels, {
              queueId: qid,
              whatsappId: data.whatsappId || whatsappId,
            })
          ) {
            return;
          }
          // guarda queueId resolvido no data para setState
          data.queueId = qid;
        }
        // active → idle residual: ok, call-ended também limpa
        if (nextStatus === "idle" || nextStatus === "ended") {
          if (dataCid) markCallEndedId(dataCid);
          nextStatus = "idle";
        }
        setCallState((s) => ({
          ...s,
          status: nextStatus,
          phone: data.phone || s.phone || "",
          name: data.name || s.name || "",
          peer: data.peer || data.name || data.phone || s.peer || "",
          callId: nextStatus === "idle" ? "" : data.callId || s.callId || "",
          seconds:
            nextStatus === "idle"
              ? 0
              : typeof data.seconds === "number"
                ? data.seconds
                : s.seconds || 0,
          inCall:
            nextStatus === "idle"
              ? false
              : Boolean(data.inCall) ||
                nextStatus === "queue_ring" ||
                nextStatus === "ivr" ||
                nextStatus === "active" ||
                nextStatus === "ringing",
          systemName: data.systemName || s.systemName || "Multiflow Voz",
          transferring: Boolean(data.transferring),
          transferTargetName: data.transferTargetName || s.transferTargetName || "",
          queueId:
            nextStatus === "idle"
              ? null
              : data.queueId != null
                ? data.queueId
                : s.queueId ?? null,
          queueLabel: nextStatus === "idle" ? null : s.queueLabel ?? null,
          remainingSec: nextStatus === "idle" ? null : s.remainingSec ?? null,
          round: nextStatus === "idle" ? null : s.round ?? null,
        }));
        callStateRef.current = {
          ...callStateRef.current,
          status: nextStatus,
          callId:
            nextStatus === "idle"
              ? ""
              : data.callId || callStateRef.current?.callId || "",
          inCall:
            nextStatus === "idle"
              ? false
              : Boolean(data.inCall) ||
                nextStatus === "queue_ring" ||
                nextStatus === "ivr" ||
                nextStatus === "active" ||
                nextStatus === "ringing",
          queueId:
            nextStatus === "idle"
              ? null
              : data.queueId != null
                ? data.queueId
                : callStateRef.current?.queueId ?? null,
        };
        if (nextStatus === "idle" && !data.transferring) {
          setTransferWaiting(null);
          stopAllVoiceSounds();
          clearVoiceDesktopAlert();
        }
        // URA never shows softphone to agent (exceto discador pinado)
        if (nextStatus === "ivr" && !agentPinnedOpenRef.current) {
          hideSoftphoneForIvrOnly(data.callId);
        }
        if (nextStatus === "queue_ring" || nextStatus === "queue_hold") {
          cancelIvrInjectRetries();
          forceAgentSoftphoneOpen();
        }
        if (nextStatus === "incoming") {
          resolveMultiflowContactLabel({
            peer: data.peer,
            peerName: data.name,
            peerLid: data.peerLid,
            phone: data.phone,
          }).then((resolved) => {
            setCallState((s) => {
              if (s.status !== "incoming") return s;
              return {
                ...s,
                name: resolved.name || data.name || "",
                phone: resolved.phone || data.phone || s.phone,
                peer: resolved.peer || data.peer || data.name || s.peer,
              };
            });
            if (resolved.name || resolved.phone) {
              postToIframeRef.current?.({
                type: "resolved-contact",
                name: resolved.name || "",
                phone: resolved.phone || "",
                peer: resolved.peer || "",
              });
            }
          });
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [
    whatsappId,
    token,
    channels,
    currentUserId,
    user,
    channelRejectsCalls,
    notifyAutoReject,
    notifyCallOnce,
    markCallAnswered,
    clearCallAnswered,
    shouldBlockQueueRing,
    injectIvrRetries,
    armIvrOnHub,
    forceAgentSoftphoneOpen,
    shouldKeepSoftphoneForAgent,
    hideSoftphoneForIvrOnly,
    cancelIvrInjectRetries,
    forceSoftphoneIdle,
    markCallEndedId,
  ]);

  useEffect(() => {
    if (!user?.id || !user?.companyId) return undefined;
    const socket = socketConnection({ user });
    const transferEv = `company-${user.companyId}-voice-transfer`;
    const incomingEv = `company-${user.companyId}-voice-incoming`;
    const endedEv = `company-${user.companyId}-voice-call-ended`;
    const queueRingEv = `company-${user.companyId}-voice-queue-ring`;
    const ticketTransferEv = `company-${user.companyId}-voice-ticket-transfer`;
    const answeredEv = `company-${user.companyId}-voice-answered`;

    const focusSoftphone = () => {
      forceAgentSoftphoneOpen();
    };

    /** Pós-URA (server ou softphone): toca e abre softphone do atendente. */
    const onVoiceQueueRing = (payload) => {
      if (!payload) return;
      const stNow = callStateRef.current?.status;
      // Transferência em curso: não misturar com toque de fila (som duplicado / UI errada)
      if (
        stNow === "transferring" ||
        stNow === "transfer_offer" ||
        stNow === "pending_transfer"
      ) {
        return;
      }
      // Originador ainda Em chamada e ticket foi pra fila: cede a mídia
      if (
        stNow === "active" &&
        payload.fromTicketTransfer &&
        payload.callId &&
        String(callStateRef.current?.callId || "") === String(payload.callId)
      ) {
        postToIframeRef.current?.({
          type: "yield-to-queue",
          callId: payload.callId,
          queueId: payload.queueId || null,
          label: payload.label || null,
        });
        return;
      }
      if (stNow === "active") {
        return;
      }
      if (shouldBlockQueueRing(payload.callId)) {
        stopAllVoiceSoundsAndSuppress();
        return;
      }
      const qid = Number(payload.queueId || 0);
      // Fila + liberação na conexão (voz UserIds) + eligibleUserIds do servidor
      if (
        !agentMayRingIvrQueue(user, channels, {
          queueId: qid,
          whatsappId: payload.whatsappId || whatsappId,
          eligibleUserIds: payload.eligibleUserIds,
        })
      ) {
        return;
      }
      if (
        payload.whatsappId &&
        channelRejectsCalls(payload.whatsappId || whatsappId)
      ) {
        return;
      }

      cancelIvrInjectRetries();
      suppressVoiceRing(false);
      unlockVoiceAudio();
      if (!isVoiceRingingPlaying()) {
        void playRinging().then((r) => {
          if (r === "blocked" && !shouldBlockQueueRing(payload.callId)) {
            setTimeout(() => {
              if (
                !shouldBlockQueueRing(payload.callId) &&
                !isVoiceRingingPlaying() &&
                agentMayRingIvrQueue(user, channels, {
                  queueId: payload.queueId,
                  whatsappId: payload.whatsappId || whatsappId,
                  eligibleUserIds: payload.eligibleUserIds,
                })
              ) {
                playRinging().catch(() => {});
              }
            }, 400);
          }
        });
      }
      forceAgentSoftphoneOpen();
      requestAnimationFrame(() => forceAgentSoftphoneOpen());

      const list = Array.isArray(channels) ? channels : [];
      const ringCh = payload.whatsappId
        ? list.find((c) => Number(c.id) === Number(payload.whatsappId)) || null
        : null;
      const isOfficial =
        String(payload.provider || "") === "official" ||
        String(ringCh?.channel || "") === "whatsapp_oficial";
      if (
        ringCh &&
        typeof onSwitchChannel === "function" &&
        agentCanUseVoiceConnection(channels, payload.whatsappId) &&
        (Number(ringCh.id) !== Number(whatsappId) ||
          String(ringCh.wavoip || "") !== String(token || ""))
      ) {
        onSwitchChannel(ringCh);
      }

      const nextQueue = {
        status: "queue_ring",
        callId: payload.callId || "",
        peer: payload.peerName || payload.phone || "",
        phone: payload.phone || "",
        name: payload.peerName || "",
        seconds: 0,
        inCall: true,
        systemName: "Multiflow Voz",
        queueId: payload.queueId || null,
        queueLabel: payload.label || null,
        remainingSec: payload.remainingSec || payload.maxWaitSec || null,
        round: payload.round || 1,
      };
      // dupla checagem: state pode ter virado active no meio do handler
      if (shouldBlockQueueRing(payload.callId)) {
        stopAllVoiceSoundsAndSuppress();
        return;
      }
      setCallState(nextQueue);
      callStateRef.current = { ...callStateRef.current, ...nextQueue };

      // Softphone: injeta fila no poll/reclaim se o iframe ainda está idle
      const injectQueue = () => {
        postToIframeRef.current?.({
          type: "server-ivr-queue-ring",
          callId: payload.callId || "",
          peerNumber: payload.phone || "",
          peerName: payload.peerName || "",
          queueId: payload.queueId || null,
          label: payload.label || null,
          provider: isOfficial ? "official" : payload.provider || "wacalls",
          claimClientId: isOfficial
            ? `server-official-ivr-${payload.callId || ""}`
            : undefined,
        });
      };
      injectQueue();
      setTimeout(injectQueue, 500);
      setTimeout(injectQueue, 1400);

      logTicketCallFromSoftphone({
        direction: "inbound",
        status: "started",
        callId: payload.callId,
        phone: payload.phone || "",
        peerLid: payload.peerLid || "",
        whatsappId: payload.whatsappId || whatsappId || undefined,
        queueId: payload.queueId || undefined,
      });

      notifyCallOnce(
        `queue-ring-${payload.callId || ""}-${payload.round || 1}`,
        `Fila${payload.label ? ` ${payload.label}` : ""}: tocando — atenda para falar com o cliente.`
      );
    };

    const onTransfer = (payload) => {
      const target = Number(payload?.targetUserId || 0);
      const me = Number(currentUserId || user.id);
      // Só aceita se o destino for EXPLICITAMENTE este usuário.
      if (!target || target !== me) return;

      const transferCallId = String(payload?.callId || "").trim();
      // Libera o callId (pode ter sido marcado quando o originador atendeu).
      if (transferCallId) clearCallAnswered(transferCallId);

      const transferWaId = Number(payload?.whatsappId || 0) || null;
      const list = Array.isArray(channels) ? channels : [];
      const targetCh = transferWaId
        ? list.find((c) => Number(c.id) === transferWaId) || null
        : null;

      // Conexão da ligação (ex. 8802) não liberada pra este atendente
      if (transferWaId && !targetCh) {
        toast.error(
          "Você recebeu uma transferência, mas não tem permissão de voz nesta conexão. Peça liberação em Conexões → Usuários de voz."
        );
        return;
      }

      toast.info(
        `📞 Transferência de ligação${
          payload?.peerName || payload?.phone
            ? ` (${payload.peerName || payload.phone})`
            : ""
        }. Abrindo softphone no canal correto…`,
        { autoClose: 10000, toastId: `mfv-transfer-${payload?.callId || "x"}` }
      );

      // Troca token/canal ANTES de abrir (senão fica no Oficial7700 e só discador)
      if (
        targetCh &&
        typeof onSwitchChannel === "function" &&
        (Number(targetCh.id) !== Number(whatsappId) ||
          String(targetCh.wavoip || "") !== String(token || ""))
      ) {
        onSwitchChannel(targetCh);
      }

      // Não trava o poll com transfer_offer cedo demais — status "incoming"/offer
      // era setado só no toast e bloqueava o tick de achar a park no 8802.
      agentPinnedOpenRef.current = true;
      suppressVoiceRing(false);
      unlockVoiceAudio();
      if (!isVoiceRingingPlaying()) {
        playRinging().catch(() => {});
      }
      forceAgentSoftphoneOpen();
      requestAnimationFrame(() => forceAgentSoftphoneOpen());

      setCallState((s) => ({
        ...s,
        // pending_transfer: UI "chegando" sem bloquear o listen multi-canal
        status: "pending_transfer",
        inCall: true,
        callId: payload?.callId || s.callId || "",
        phone: payload?.phone || s.phone,
        name: payload?.peerName || s.name,
        peer: payload?.peerName || payload?.phone || s.peer,
      }));
      callStateRef.current = {
        ...callStateRef.current,
        status: "pending_transfer",
        callId: payload?.callId || callStateRef.current?.callId || "",
        inCall: true,
      };

      const isOfficial =
        String(targetCh?.channel || "") === "whatsapp_oficial" ||
        String(payload?.provider || "") === "official";
      const injectOffer = () => {
        postToIframeRef.current?.({
          type: isOfficial ? "official-transfer-offer" : "transfer-offer",
          callId: payload.callId,
          phone: payload.phone || "",
          peerNumber: payload.phone || "",
          peerName: payload.peerName || "",
          peerLid: payload.peerLid || "",
          fromUserId: payload.fromUserId || null,
          targetUserId: payload.targetUserId || null,
          provider: isOfficial ? "official" : payload?.provider || "wacalls",
        });
      };
      if (payload?.callId) {
        setTimeout(injectOffer, 200);
        setTimeout(injectOffer, 800);
        setTimeout(injectOffer, 1800);
        setTimeout(injectOffer, 3200);
      }
    };

    // Push do backend no connect/ring — funciona com navegador minimizado
    // (não depende do setInterval engasgado do Chrome no iframe).
    const onVoiceIncoming = (payload) => {
      if (!payload?.callId) return;
      if (channelRejectsCalls(payload.whatsappId || whatsappId)) {
        notifyAutoReject({
          phone: payload.phone || "",
          callId: payload.callId || null,
          peerName: payload.peerName || null,
          waId: payload.whatsappId,
        });
        return;
      }

      const sameChannel =
        Number(payload.whatsappId) === Number(whatsappId) ||
        String(payload.token || "") === String(token || "");
      // Canal sem permissão voiceUserIds: não troca e não toca
      if (
        payload.whatsappId &&
        !agentCanUseVoiceConnection(channels, payload.whatsappId)
      ) {
        return;
      }
      if (
        !sameChannel &&
        payload.token &&
        typeof onSwitchChannel === "function"
      ) {
        const allowedCh = (Array.isArray(channels) ? channels : []).find(
          (c) => Number(c.id) === Number(payload.whatsappId)
        );
        if (allowedCh) onSwitchChannel(allowedCh);
      }

      const isIvr = Boolean(payload.voiceIvr);
      const ch =
        (Array.isArray(channels) ? channels : []).find(
          (c) =>
            Number(c.id) === Number(payload.whatsappId || whatsappId)
        ) || null;
      const ivrCfg =
        buildChannelVoiceIvrForSoftphone(
          ch,
          String(process.env.REACT_APP_BACKEND_URL || "").replace(
            /\/$/,
            ""
          )
        ) ||
        (payload.voiceIvr
          ? { enabled: true, ...payload.voiceIvr }
          : null);
      const useIvr = isIvr || Boolean(ivrCfg);

      if (useIvr) {
        if (shouldKeepSoftphoneForAgent(payload.callId)) {
          return;
        }
        if (agentPinnedOpenRef.current || openRef.current) {
          if (payload.callId) armIvrOnHub(payload.callId, payload.whatsappId);
          return;
        }
        if (!hideSoftphoneForIvrOnly(payload.callId)) {
          return;
        }
        setCallState({
          status: "ivr",
          callId: payload.callId || "",
          peer: payload.peerName || payload.phone || "WhatsApp",
          phone: payload.phone || "",
          name: payload.peerName || "",
          seconds: 0,
          inCall: true,
          systemName: "Multiflow Voz",
        });
        if (payload.callId) armIvrOnHub(payload.callId, payload.whatsappId);
        if (ivrCfg) {
          injectIvrRetries({
            type: "inject-ivr-config",
            callId: payload.callId || "",
            peerNumber: payload.phone || "",
            peerName: payload.peerName || "",
            provider:
              String(payload.provider || "") === "official"
                ? "official"
                : "wacalls",
            voiceIvr: ivrCfg,
            quiet: true,
          });
        }
        return;
      }

      focusSoftphone();
      unlockVoiceAudio();
      maybeRequestVoiceNotifyOnGesture();
      ensureVoiceNotificationPermission().catch(() => {});
      setCallState({
        status: "incoming",
        callId: payload.callId || "",
        peer: payload.peerName || payload.phone || "WhatsApp",
        phone: payload.phone || "",
        name: payload.peerName || "",
        seconds: 0,
        inCall: true,
        systemName: "Multiflow Voz",
      });

      showVoiceDesktopNotification({
        kind: "incoming",
        callId: payload.callId || "",
        name: payload.peerName || "",
        phone: payload.phone || "",
        peer: payload.peerName || payload.phone || "",
        onClick: focusSoftphone,
      });

      notifyCallOnce(
        payload.callId || payload.phone,
        `Ligação: ${payload.peerName || payload.phone || "WhatsApp"}`
      );
    };

    const onVoiceEnded = (payload) => {
      if (!payload?.callId) return;
      const endedId = String(payload.callId);
      markCallEndedId(endedId);
      const curId = String(callStateRef.current?.callId || "");
      // Só encerra a UI se for a mesma ligação (ou ainda sem callId no parent)
      if (curId && curId !== endedId) return;
      forceSoftphoneIdle(endedId);
    };

    /** Outro atendente atendeu fila/transfer — para toque nesta tela. */
    const onVoiceAnswered = (payload) => {
      if (!payload?.callId) return;
      const cid = String(payload.callId);
      const me = Number(currentUserId || user?.id || 0);
      const from = Number(payload.fromUserId || 0);
      const st = callStateRef.current?.status;
      const cur = String(callStateRef.current?.callId || "");
      // Só marca "já atendida" se ESTE cliente estava tocando/nessa ligação.
      // Idle em outras telas NÃO pode envenenar answeredCallIds — senão a
      // transferência com o mesmo callId morre no destino (Denise).
      const wasRingingThis =
        st === "queue_ring" ||
        st === "queue_hold" ||
        st === "transfer_offer" ||
        st === "pending_transfer" ||
        st === "incoming" ||
        (st === "transferring" && (!cur || cur === cid));
      if (wasRingingThis || (from && me && from === me)) {
        markCallAnswered(cid);
      } else {
        quietVoiceRing(8000);
      }
      // Quem atendeu já ficou active via call-started
      if (from && me && from === me) return;
      if (cur && cur !== cid) return;
      if (wasRingingThis) {
        stopAllVoiceSoundsAndSuppress();
        clearVoiceDesktopAlert();
        setTransferWaiting(null);
        const idle = {
          status: "idle",
          inCall: false,
          callId: "",
          transferring: false,
          transferTargetName: "",
          queueId: null,
          queueLabel: null,
        };
        setCallState((s) => ({ ...s, ...idle }));
        callStateRef.current = { ...callStateRef.current, ...idle };
        try {
          postToIframeRef.current?.({
            type: "transfer-cancel",
          });
          postToIframeRef.current?.({
            type: "queue-answered-elsewhere",
            callId: cid,
          });
          if (st === "transferring") {
            postToIframeRef.current?.({
              type: "transfer-taken",
              callId: cid,
            });
          }
        } catch {
          /* ignore */
        }
      }
    };

    const onVoiceTicketTransfer = (payload) => {
      if (!payload) return;
      const me = Number(currentUserId || user.id);
      const target = Number(payload.targetUserId || 0);
      if (target && target === me) return;
      const st = callStateRef.current?.status;
      const live =
        st === "active" ||
        st === "ivr" ||
        st === "queue_hold" ||
        st === "queue_ring";
      if (!live || !target) return;
      const curCall = String(callStateRef.current?.callId || "");
      if (
        payload.callId &&
        curCall &&
        curCall !== String(payload.callId)
      ) {
        const a = String(callStateRef.current?.phone || "").replace(/\D/g, "").slice(-8);
        const b = String(payload.phone || "").replace(/\D/g, "").slice(-8);
        if (a && b && a !== b) return;
      }
      postToIframeRef.current?.({
        type: "transfer-target",
        targetUserId: target,
        fromUserId: me,
        targetName: payload.targetName || "",
      });
    };

    if (typeof socket?.on === "function") {
      socket.on(transferEv, onTransfer);
      socket.on(incomingEv, onVoiceIncoming);
      socket.on(endedEv, onVoiceEnded);
      socket.on(queueRingEv, onVoiceQueueRing);
      socket.on(ticketTransferEv, onVoiceTicketTransfer);
      socket.on(answeredEv, onVoiceAnswered);
    }
    return () => {
      if (typeof socket?.off === "function") {
        socket.off(transferEv, onTransfer);
        socket.off(incomingEv, onVoiceIncoming);
        socket.off(endedEv, onVoiceEnded);
        socket.off(queueRingEv, onVoiceQueueRing);
        socket.off(ticketTransferEv, onVoiceTicketTransfer);
        socket.off(answeredEv, onVoiceAnswered);
      }
      stopAllVoiceSounds();
      clearVoiceDesktopAlert();
    };
  }, [
    user,
    currentUserId,
    whatsappId,
    token,
    channels,
    onSwitchChannel,
    channelRejectsCalls,
    notifyAutoReject,
    armIvrOnHub,
    injectIvrRetries,
    notifyCallOnce,
    shouldBlockQueueRing,
    forceAgentSoftphoneOpen,
    shouldKeepSoftphoneForAgent,
    cancelIvrInjectRetries,
    hideSoftphoneForIvrOnly,
    forceSoftphoneIdle,
    markCallEndedId,
    markCallAnswered,
    clearCallAnswered,
    quietVoiceRing,
  ]);

  const postToIframe = useCallback((payload) => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage({ target: "multiflow-voz-softphone", ...payload }, "*");
  }, []);
  postToIframeRef.current = postToIframe;

  const clampPosition = useCallback((x, y, el) => {
    const w = el?.offsetWidth || 320;
    const h = el?.offsetHeight || 64;
    const maxX = Math.max(0, window.innerWidth - w);
    const maxY = Math.max(0, window.innerHeight - h);
    return {
      x: Math.max(0, Math.min(x, maxX)),
      y: Math.max(0, Math.min(y, maxY)),
    };
  }, []);

  const startDrag = (e) => {
    if (e.button !== 0) return;
    // não iniciar drag em botões
    if (e.target?.closest?.("button")) return;
    maybeRequestVoiceNotifyOnGesture();
    const el = document.getElementById(WIDGET_ID);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (!position) {
      const initial = { x: rect.left, y: rect.top };
      setPosition(initial);
      positionRef.current = initial;
    }
    setDragging(true);
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    didDragRef.current = false;
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      const el = document.getElementById(WIDGET_ID);
      const next = clampPosition(e.clientX - dragOffset.x, e.clientY - dragOffset.y, el);
      positionRef.current = next;
      setPosition(next);
      didDragRef.current = true;
    };
    const onUp = () => {
      setDragging(false);
      const pos = positionRef.current;
      if (pos) {
        try {
          localStorage.setItem(POS_KEY, JSON.stringify(pos));
        } catch {
          /* ignore */
        }
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [dragging, dragOffset, clampPosition]);

  useEffect(() => {
    if (!position) return undefined;
    const onResize = () => {
      const el = document.getElementById(WIDGET_ID);
      const next = clampPosition(position.x, position.y, el);
      if (next.x !== position.x || next.y !== position.y) {
        positionRef.current = next;
        setPosition(next);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [position, clampPosition]);

  if (!token || !src) return null;

  // Toque de fila/recebida: painel SEMPRE expandido (som já toca no pai;
  // se open/minimize falhar por race, ainda assim o softphone aparece).
  const agentMustSee =
    callState.status === "queue_ring" ||
    callState.status === "queue_hold" ||
    callState.status === "incoming" ||
    callState.status === "transfer_offer" ||
    callState.status === "pending_transfer";
  const panelOpen = open || agentMustSee;
  const showMinimized = minimized && !agentMustSee;

  const style = position ? { left: position.x, top: position.y } : undefined;
  const peerLabel =
    callState.name ||
    callState.peer ||
    callState.phone ||
    "—";

  const hiddenListenerStyle = {
    position: "fixed",
    width: 1,
    height: 1,
    opacity: 0,
    pointerEvents: "none",
    overflow: "hidden",
    bottom: 0,
    right: 0,
    zIndex: -1,
    border: 0,
    margin: 0,
    padding: 0,
  };

  return (
    <div
      id={WIDGET_ID}
      data-testid="multiflow-voz-widget"
      className={
        panelOpen
          ? [
              classes.root,
              !position ? classes.rootDefault : "",
              showMinimized ? classes.rootMinimized : classes.rootExpanded,
              !showMinimized && coexOutboundOnly ? classes.rootExpandedCoex : "",
            ]
              .filter(Boolean)
              .join(" ")
          : undefined
      }
      style={
        panelOpen
          ? {
              ...(style || {}),
              zIndex: agentMustSee ? 20000 : 13000,
            }
          : hiddenListenerStyle
      }
    >
      {panelOpen && !showMinimized && (
        <div className={classes.chrome} onMouseDown={startDrag}>
          <div className={classes.chromeTitle}>
            {callState.systemName || "Multiflow Voz"}
          </div>
          <div className={classes.chromeActions}>
            <button
              type="button"
              className={classes.chromeBtn}
              title="Minimizar"
              aria-label="Minimizar"
              onClick={() => {
                if (agentMustSee) {
                  // Em toque: só permite mini badge, não some
                  setMinimized(true);
                  setOpen(true);
                  return;
                }
                setMinimized(true);
              }}
            >
              −
            </button>
            <button
              type="button"
              className={classes.chromeBtn}
              title="Fechar"
              aria-label="Fechar"
              onClick={() => {
                if (agentMustSee) {
                  toast.info(
                    "Há uma ligação tocando — use Atender ou Recusar no softphone."
                  );
                  forceAgentSoftphoneOpen();
                  return;
                }
                // Não hangup de URA em background só por fechar o discador
                if (
                  callState.inCall &&
                  callState.status !== "ivr" &&
                  callState.status !== "idle"
                ) {
                  postToIframe({ type: "hangup" });
                }
                agentPinnedOpenRef.current = false;
                setOpen(false);
                setMinimized(false);
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {panelOpen && !showMinimized && coexOutboundOnly && (
        <p className={classes.coexBanner}>
          COEX: o sistema só <strong>faz</strong> ligação. Recebidas tocam no
          celular — atenda no WhatsApp Business ou retorne pelo softphone.
        </p>
      )}

      {panelOpen && !showMinimized && callState.status === "queue_ring" && (
        <div className={classes.transferWaiting}>
          <span className={classes.transferWaitingDot} aria-hidden="true" />
          <div className={classes.transferWaitingText}>
            <div className={classes.transferWaitingTitle}>
              {callState.queueLabel
                ? `Fila: ${callState.queueLabel}`
                : "Fila de atendimento"}
            </div>
            <div className={classes.transferWaitingSub}>
              Cliente aguardando. Clique em Atender para falar.
            </div>
          </div>
          <button
            type="button"
            className={classes.answerBtn}
            onClick={() => {
              markCallAnswered(callState.callId);
              postToIframeRef.current?.({ type: "queue-hold-answer" });
            }}
          >
            Atender
          </button>
        </div>
      )}

      {panelOpen &&
        !showMinimized &&
        (callState.status === "pending_transfer" ||
          callState.status === "transfer_offer") && (
        <div className={classes.transferWaiting}>
          <span className={classes.transferWaitingDot} aria-hidden="true" />
          <div className={classes.transferWaitingText}>
            <div className={classes.transferWaitingTitle}>
              Transferência recebida
            </div>
            <div className={classes.transferWaitingSub}>
              {callState.name || callState.peer || callState.phone
                ? `${callState.name || callState.peer || callState.phone} — clique em Atender`
                : "Clique em Atender para falar com o cliente."}
            </div>
          </div>
          <button
            type="button"
            className={classes.answerBtn}
            onClick={() => {
              clearCallAnswered(callState.callId);
              suppressVoiceRing(false);
              unlockVoiceAudio();
              const list = Array.isArray(channels) ? channels : [];
              const ch =
                list.find((c) => Number(c.id) === Number(whatsappId)) || null;
              const isOfficial =
                String(ch?.channel || "") === "whatsapp_oficial";
              // Garante oferta no iframe antes do Atender (evita só discador)
              postToIframeRef.current?.({
                type: isOfficial
                  ? "official-transfer-offer"
                  : "transfer-offer",
                callId: callState.callId || "",
                phone: callState.phone || "",
                peerNumber: callState.phone || "",
                peerName: callState.name || callState.peer || "",
                provider: isOfficial ? "official" : "wacalls",
              });
              postToIframeRef.current?.({
                type: "transfer-answer",
                callId: callState.callId || "",
              });
            }}
          >
            Atender
          </button>
        </div>
      )}

      {panelOpen && !showMinimized && transferWaiting && !Array.isArray(transferTargets) && (
        <div className={classes.transferWaiting}>
          <span className={classes.transferWaitingDot} aria-hidden="true" />
          <div className={classes.transferWaitingText}>
            <div className={classes.transferWaitingTitle}>
              Transferindo…
            </div>
            <div className={classes.transferWaitingSub}>
              {transferWaiting.targetName
                ? `Aguardando ${transferWaiting.targetName} atender a ligação.`
                : "Aguardando o atendente atender a ligação."}
            </div>
          </div>
        </div>
      )}

      {panelOpen && !showMinimized && Array.isArray(transferTargets) && (
        <div className={classes.transferPick}>
          <div className={classes.transferPickTitle}>
            Transferir ligação para:
          </div>
          <input
            className={classes.transferSearch}
            type="search"
            value={transferSearch}
            onChange={(e) => setTransferSearch(e.target.value)}
            placeholder="Buscar usuário…"
            autoFocus
            autoComplete="off"
          />
          <div className={classes.transferPickList}>
            {transferTargets
              .filter((u) => {
                const q = transferSearch.trim().toLowerCase();
                if (!q) return true;
                const label = `${u.name || ""} ${u.email || ""} ${u.id}`.toLowerCase();
                return label.includes(q);
              })
              .map((u) => (
                <button
                  key={u.id}
                  type="button"
                  className={classes.transferPickBtn}
                  onClick={() => {
                    const targetName = u.name || u.email || `#${u.id}`;
                    postToIframe({
                      type: "transfer-target",
                      targetUserId: u.id,
                      fromUserId: currentUserId || user?.id || null,
                      targetName,
                    });
                    setTransferWaiting({
                      targetName,
                      peerName: transferPickMeta?.peerName || "",
                      phone: transferPickMeta?.phone || "",
                    });
                    setTransferTargets(null);
                    setTransferPickMeta(null);
                    setTransferSearch("");
                    setOpen(true);
                    setMinimized(false);
                  }}
                >
                  {u.name || u.email || `#${u.id}`}
                  {u.online ? " · online" : ""}
                </button>
              ))}
            {transferTargets.filter((u) => {
              const q = transferSearch.trim().toLowerCase();
              if (!q) return false;
              const label = `${u.name || ""} ${u.email || ""} ${u.id}`.toLowerCase();
              return label.includes(q);
            }).length === 0 && transferSearch.trim() ? (
              <div className={classes.transferPickEmpty}>
                Nenhum usuário encontrado para “{transferSearch.trim()}”.
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className={classes.transferPickBtn}
            onClick={() => {
              postToIframe({ type: "transfer-cancel" });
              setTransferTargets(null);
              setTransferPickMeta(null);
              setTransferSearch("");
            }}
          >
            Cancelar
          </button>
        </div>
      )}

      <div
        className={
          panelOpen && !showMinimized
            ? classes.iframeWrap
            : panelOpen && showMinimized
              ? classes.iframeHidden
              : undefined
        }
        style={
          !panelOpen
            ? { width: 1, height: 1, overflow: "hidden" }
            : undefined
        }
        aria-hidden={showMinimized || !panelOpen}
      >
        <iframe
          ref={iframeRef}
          title="Softphone Multiflow Voz"
          className={panelOpen ? classes.iframe : undefined}
          style={
            !panelOpen
              ? { width: 1, height: 1, border: 0, opacity: 0 }
              : undefined
          }
          src={src}
          allow="microphone; autoplay"
          // Evita TAB/focus no iframe fantasma roubar o input do ticket
          // ou a busca de contatos (CRM overlay)
          tabIndex={
            !panelOpen || showMinimized || dialerContactOpen ? -1 : 0
          }
          aria-hidden={
            !panelOpen || showMinimized || dialerContactOpen
              ? "true"
              : undefined
          }
        />
        {panelOpen && !showMinimized && dialerContactOpen ? (
          <div className={classes.contactOverlay}>
            <DialerContactPicker
              open={dialerContactOpen}
              onClose={() => setDialerContactOpen(false)}
              onSelect={(contact) => {
                setDialerContactOpen(false);
                postToIframe({
                  type: "set-phone",
                  phone: contact.phone || "",
                  name: contact.name || "",
                });
                setCallState((s) => ({
                  ...s,
                  phone: contact.phone || s.phone,
                  name: contact.name || s.name,
                  peer: contact.name || contact.phone || s.peer,
                }));
                setOpen(true);
                setMinimized(false);
              }}
            />
          </div>
        ) : null}
      </div>

      {panelOpen && showMinimized && (
        <div
          className={classes.mini}
          onMouseDown={startDrag}
          onClick={() => {
            if (didDragRef.current) {
              didDragRef.current = false;
              return;
            }
            setMinimized(false);
            requestAnimationFrame(() => {
              setTimeout(() => {
                iframeRef.current?.focus?.();
                postToIframe({ type: "focus" });
              }, 80);
            });
          }}
          title="Clique para expandir · arraste para mover"
        >
          <span
            className={[
              classes.miniDot,
              callState.status === "incoming" ||
                callState.status === "ringing" ||
                callState.status === "ivr" ||
                callState.status === "queue_ring" ||
                callState.status === "queue_hold"
                ? classes.miniDotRinging
                : "",
              callState.status === "active" ? classes.miniDotActive : "",
            ]
              .filter(Boolean)
              .join(" ")}
          />
          <div className={classes.miniText}>
            <div className={classes.miniStatus}>{statusLabel(callState)}</div>
            <div className={classes.miniPeer}>{peerLabel}</div>
          </div>
          {callState.inCall &&
          callState.status !== "incoming" &&
          callState.status !== "queue_ring" ? (
            <>
              <div className={classes.miniTimer}>{formatTimer(callState.seconds)}</div>
              <button
                type="button"
                className={classes.miniHangup}
                title="Encerrar"
                aria-label="Encerrar chamada"
                onClick={(e) => {
                  e.stopPropagation();
                  postToIframe({ type: "hangup" });
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                📞
              </button>
            </>
          ) : callState.status === "queue_ring" ? (
            <button
              type="button"
              className={classes.miniHangup}
              style={{ background: "#16a34a" }}
              title="Atender fila"
              aria-label="Atender"
              onClick={(e) => {
                e.stopPropagation();
                markCallAnswered(callState.callId);
                postToIframe({ type: "queue-hold-answer" });
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              📞
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default MultiflowVozWidget;
